import { useState, useCallback, useEffect, useMemo, useDeferredValue, useRef } from 'react';
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polygon, Polyline, Tooltip } from 'react-leaflet';
import { MapLayerTile, MapLayerToggle, useMapLayer } from '@/components/map/MapLayerToggle';
import { MapSearchBox, RecenterMap } from '@/components/map/MapSearchBox';
import {
  NEARBY_SOURCES,
  FitBoundsOnChange,
  NearbyLayerPanel,
  NearbyPointMarkers,
  NearestPlaces,
  buildDirectionsUrl,
  formatDistance,
  openInNewTab,
  pinIcon,
  toCoord,
  useNearbyPoints,
  useNearest,
  usePersistedFlag,
} from '@/components/map/NearbyLayers';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Textarea } from "@/components/ui/textarea";
import DebouncedInput from "@/components/ui/debounced-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { TreePine, Plus, Edit, Trash2, Baby, Search, ArrowUpDown, LayoutGrid, List as ListIcon, Map as MapIcon, Users, BarChart3, Lightbulb, Home, Trash, Footprints, Dumbbell, Droplet, ParkingCircle, Sprout, Droplets, HardHat, CheckCircle, X, Calendar, Camera, FileText, ChevronLeft, ChevronRight, Eye, Navigation } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { LocationMarker } from '../location-tracking/LocationMarker';
import { DrawingMarker } from '../location-tracking/DrawingMarker';
import ActiveDelegationsCard from '../ActiveDelegationsCard';
import { REVISION_TYPES } from '@/constants/revisionTypes';
import { useRoleFlags } from '@/auth/useRoleFlags';
import { normalizeSearchText } from '@/utils/stringUtils';
import { mediaUrl } from '@/utils/mediaUrl';

// Park haritasında açılıp kapanan referans katmanları.
const NEARBY_LAYER_KEYS = ['pasif', 'protokol'];

// Custom Park Bench Icon
const BenchIcon = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="11" width="18" height="2" rx="1"/>
    <rect x="3" y="7" width="18" height="2" rx="1"/>
    <line x1="5" y1="13" x2="5" y2="20"/>
    <line x1="7" y1="9" x2="7" y2="20"/>
    <line x1="19" y1="13" x2="19" y2="20"/>
    <line x1="17" y1="9" x2="17" y2="20"/>
    <line x1="4" y1="20" x2="8" y2="20"/>
    <line x1="16" y1="20" x2="20" y2="20"/>
  </svg>
);

// Custom Light Pole Icon (Elektrik Direği) - Ters L Şeklinde, Belirgin, Ampul Vurgulu
const LightPoleIcon = ({ className }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className={className}
    >
      {/* Yatay kol (üstte) - Gri ton */}
      <line x1="4" y1="4" x2="12" y2="4" strokeWidth="3.5" stroke="#6b7280" opacity="0.8"/>
      
      {/* Dikey direk - Koyu renk */}
      <line x1="12" y1="4" x2="12" y2="20" strokeWidth="3.5" stroke="#374151" opacity="1"/>
      
      {/* Ampul dış halkası (büyük, belirgin, sarı) */}
      <circle cx="4" cy="4" r="4" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1.5"/>
      
      {/* Ampul içi (parlak sarı) */}
      <circle cx="4" cy="4" r="2.5" fill="#fcd34d"/>
      
      {/* Ampul içindeki parlaklık efekti (beyaz) */}
      <circle cx="3.3" cy="3.3" r="1" fill="white" opacity="0.8"/>
      
      {/* Işık ışınları (ampulden yayılan, sarı) */}
      <line x1="1" y1="4" x2="0" y2="2" stroke="#fbbf24" strokeWidth="2" opacity="0.7"/>
      <line x1="4" y1="1" x2="4" y2="0" stroke="#fbbf24" strokeWidth="2" opacity="0.7"/>
      <line x1="7" y1="4" x2="8" y2="2" stroke="#fbbf24" strokeWidth="2" opacity="0.7"/>
      <line x1="4" y1="7" x2="4" y2="8" stroke="#fbbf24" strokeWidth="2" opacity="0.7"/>
      
      {/* Bağlantı noktası (direk ve kolun birleştiği yer) - Koyu gri */}
      <circle cx="12" cy="4" r="2" fill="#4b5563" opacity="0.9"/>
      
      {/* Alt taban (direk dibi) - Koyu */}
      <rect x="10" y="20" width="4" height="2.5" fill="#374151" opacity="0.8" rx="1"/>
      <rect x="9" y="22" width="6" height="1" fill="#1f2937" opacity="0.6" rx="0.5"/>
    </svg>
  );
};

// Park Revize Sekmesi Component'i
const ParkRevisionTab = ({
  parkId,
  parkName,
  isFormReadOnly,
  API,
  revisions = [],
  parks = [],
  onDeleteRevision,
  onDeleteCompletedRevisionHistory,
  userRole,
  personnelId
}) => {
  const [newRevision, setNewRevision] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    photos: {
      before: [], // Eski fotoğraflar
      after: []   // Yeni fotoğraflar
    }
  });
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [parkRevisions, setParkRevisions] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxCurrentIndex, setLightboxCurrentIndex] = useState(0);
  const [editingRevisionId, setEditingRevisionId] = useState(null);
  const [editingRevision, setEditingRevision] = useState(null);
  const [editingRevisionPhotos, setEditingRevisionPhotos] = useState({ before: [], after: [] });
  const [uploadingEditPhotos, setUploadingEditPhotos] = useState(false);
  const [historyDeleteDialog, setHistoryDeleteDialog] = useState({
    open: false,
    step: 1,
    revisionId: null,
    category: "",
  });

  const openHistoryDeleteDialog = (rev) => {
    setHistoryDeleteDialog({
      open: true,
      step: 1,
      revisionId: rev?.revision_id || null,
      category: rev?.category || rev?.title || "",
    });
  };

  const closeHistoryDeleteDialog = () => {
    setHistoryDeleteDialog({ open: false, step: 1, revisionId: null, category: "" });
  };

  const handleConfirmHistoryDelete = async () => {
    if (!onDeleteCompletedRevisionHistory || !parkId || !historyDeleteDialog.revisionId) {
      closeHistoryDeleteDialog();
      return;
    }
    await onDeleteCompletedRevisionHistory(parkId, historyDeleteDialog.revisionId, {
      skipBrowserConfirm: true,
    });
    closeHistoryDeleteDialog();
  };

  // Sprint 2 F3: Dinamik yetki context'i (rol flag'leri + permission)
  const {
    has,
    isAdmin,
    isMudur,
    isMudurYardimcisi,
    isBolgeMuhendisi,
    isFormen,
    isParkGorevlisi,
    isGolgeAdmin,
  } = useRoleFlags(userRole);

  // Yetki kontrolü: revize düzenleme — yönetici kademesi
  const canEditRevisions = has('revizyon.onayla') || isAdmin || isMudur || isMudurYardimcisi;

  // Park revizelerini yükle
  useEffect(() => {
    if (!parkId) return;
    
    console.log("useEffect triggered - parkId:", parkId, "parks length:", parks.length);
    
    // Aktif revizelerden tamamlanmış olanlar
    const activeCompletedRevisions = revisions.filter(
      r => r.park_id === parkId && r.status === "Tamamlandı"
    );
    
    // Park'ın kalıcı geçmişindeki tamamlanmış revizeler
    const currentPark = parks.find(p => p.id === parkId);
    console.log("Current park:", currentPark);
    const historyRevisions = currentPark?.completed_revisions_history || [];
    console.log("History revisions from parks prop:", historyRevisions);
    
    // Aktif revizelerin ID'leri (duplicate önlemek için)
    const activeIds = new Set(activeCompletedRevisions.map(r => r.id));
    
    // Sadece geçmişte olup aktif revizelerde olmayan kayıtları ekle
    const uniqueHistoryRevisions = historyRevisions.filter(
      h => !activeIds.has(h.revision_id)
    );
    
    // Tüm tamamlanmış revizeleri birleştir
    const allRevisions = [
      ...activeCompletedRevisions.map(r => ({
        ...r,
        isActive: true,
        revision_date: r.completed_at
      })),
      ...uniqueHistoryRevisions.map(h => ({
        ...h,
        isActive: false,
        revision_date: h.revision_date || h.completed_at
      }))
    ].sort((a, b) => {
      const dateA = new Date(a.revision_date || 0);
      const dateB = new Date(b.revision_date || 0);
      return dateB - dateA; // En yeni önce
    });
    
    console.log("Setting park revisions:", allRevisions);
    setParkRevisions(allRevisions);
  }, [parkId, revisions, parks]);

  // Fotoğraf yükleme - Eskisi veya Yenisi
  const handlePhotoUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const currentPhotos = newRevision.photos[type] || [];
    if (files.length + currentPhotos.length > 10) {
      toast.error("Maksimum 10 fotoğraf yükleyebilirsiniz");
      return;
    }
    
    setUploadingPhotos(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/api/upload-revision-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return response.data.url;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      setNewRevision(prev => ({
        ...prev,
        photos: {
          ...prev.photos,
          [type]: [...(prev.photos[type] || []), ...uploadedUrls]
        }
      }));
      
      toast.success(`${uploadedUrls.length} fotoğraf yüklendi`);
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error("Fotoğraf yüklenirken hata oluştu");
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Revize kaydetme
  const handleSaveRevision = async () => {
    console.log("=== handleSaveRevision called ===");
    console.log("newRevision:", newRevision);
    console.log("parkId:", parkId);
    
    if (!newRevision.date || !newRevision.category) {
      toast.error("Lütfen tarih ve kategori seçin");
      return;
    }
    
    if (!parkId) {
      toast.error("Park ID bulunamadı");
      return;
    }
    
    try {
      // Park'ın revision_history'sine ekle
      const revisionEntry = {
        revision_id: `rev_${Date.now()}`,
        revision_date: newRevision.date,
        category: newRevision.category,
        description: newRevision.description || '',
        photos: newRevision.photos || { before: [], after: [] },
        created_at: new Date().toISOString()
      };
      
      console.log("revisionEntry:", revisionEntry);
      
      // Backend'e kaydet
      const currentPark = parks.find(p => p.id === parkId);
      console.log("currentPark:", currentPark);
      const currentHistory = currentPark?.completed_revisions_history || [];
      console.log("currentHistory:", currentHistory);
      
      const updateData = {
        completed_revisions_history: [...currentHistory, revisionEntry]
      };
      console.log("updateData:", updateData);
      
      const apiUrl = `${API}/api/parks/${parkId}`;
      console.log(`PUT ${apiUrl}`);
      console.log("Request payload:", JSON.stringify(updateData, null, 2));
      
      const response = await axios.put(apiUrl, updateData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log("Response status:", response.status);
      console.log("Response data:", response.data);
      
      if (response.data) {
        console.log("Response data:", response.data);
        console.log("Response completed_revisions_history:", response.data.completed_revisions_history);
        
        toast.success("Revize kaydı eklendi");
        
        // Response'dan gelen park verisini kullanarak revizeleri güncelle
        const updatedPark = response.data;
        const updatedHistory = updatedPark.completed_revisions_history || [];
        console.log("Updated history from response:", updatedHistory);
        
        // Aktif revizelerden tamamlanmış olanlar
        const activeCompletedRevisions = revisions.filter(
          r => r.park_id === parkId && r.status === "Tamamlandı"
        );
        
        // Aktif revizelerin ID'leri (duplicate önlemek için)
        const activeIds = new Set(activeCompletedRevisions.map(r => r.id));
        
        // Sadece geçmişte olup aktif revizelerde olmayan kayıtları ekle
        const uniqueHistoryRevisions = updatedHistory.filter(
          h => !activeIds.has(h.revision_id)
        );
        
        console.log("Active completed revisions:", activeCompletedRevisions);
        console.log("Unique history revisions:", uniqueHistoryRevisions);
        
        // Tüm tamamlanmış revizeleri birleştir
        const allRevisions = [
          ...activeCompletedRevisions.map(r => ({
            ...r,
            isActive: true,
            revision_date: r.completed_at
          })),
          ...uniqueHistoryRevisions.map(h => ({
            ...h,
            isActive: false,
            revision_date: h.revision_date || h.completed_at
          }))
        ].sort((a, b) => {
          const dateA = new Date(a.revision_date || 0);
          const dateB = new Date(b.revision_date || 0);
          return dateB - dateA; // En yeni önce
        });
        
        console.log("All revisions to set:", allRevisions);
        setParkRevisions(allRevisions);
        
        // Formu sıfırla
        setNewRevision({
          date: new Date().toISOString().split('T')[0],
          category: '',
          description: '',
          photos: {
            before: [],
            after: []
          }
        });
        
        // Parks listesini yenilemek için parent component'e bildir
        // Custom event ile parent component'e bildir
        window.dispatchEvent(new CustomEvent('refreshParks'));
      }
    } catch (error) {
      console.error("Save revision error:", error);
      console.error("Error response:", error.response);
      const errorMessage = error.response?.data?.detail || error.message || "Revize kaydedilirken hata oluştu";
      toast.error(`Hata: ${errorMessage}`);
    }
  };

  // Fotoğraf silme - Eskisi veya Yenisi
  const handleRemovePhoto = (type, index) => {
    setNewRevision(prev => ({
      ...prev,
      photos: {
        ...prev.photos,
        [type]: prev.photos[type].filter((_, i) => i !== index)
      }
    }));
  };

  // Revize kaydını düzenleme için açma
  const handleEditRevision = (rev) => {
    if (!canEditRevisions) {
      toast.error("Bu işlemi yapmak için yetkiniz bulunmamaktadır");
      return;
    }
    
    setEditingRevisionId(rev.revision_id);
    setEditingRevision({
      revision_id: rev.revision_id,
      date: rev.revision_date || rev.completed_at || new Date().toISOString().split('T')[0],
      category: rev.category || rev.title || '',
      description: rev.description || ''
    });
    
    // Fotoğrafları yükle
    const photosBefore = Array.isArray(rev.photos) ? [] : (rev.photos?.before || []);
    const photosAfter = Array.isArray(rev.photos) ? [] : (rev.photos?.after || []);
    setEditingRevisionPhotos({
      before: [...photosBefore],
      after: [...photosAfter]
    });
  };

  // Revize kaydını kaydetme
  const handleSaveEditedRevision = async () => {
    if (!parkId || !editingRevisionId) return;
    
    if (!editingRevision.date || !editingRevision.category) {
      toast.error("Lütfen tarih ve kategori girin");
      return;
    }
    
    setUploadingEditPhotos(true);
    try {
      // Park'ın mevcut revize geçmişini al
      const currentPark = parks.find(p => p.id === parkId);
      const currentHistory = currentPark?.completed_revisions_history || [];
      
      // İlgili revize kaydını bul ve güncelle
      const updatedHistory = currentHistory.map(rev => {
        if (rev.revision_id === editingRevisionId) {
          // Düzenleme geçmişi oluştur
          const editHistory = rev.edit_history || [];
          const newEditEntry = {
            edited_at: new Date().toISOString(),
            edited_by: userRole,
            changes: {
              date: rev.revision_date !== editingRevision.date ? { from: rev.revision_date, to: editingRevision.date } : null,
              category: rev.category !== editingRevision.category ? { from: rev.category, to: editingRevision.category } : null,
              description: rev.description !== editingRevision.description ? { from: rev.description, to: editingRevision.description } : null,
              photos_before_count: (rev.photos?.before || []).length !== editingRevisionPhotos.before.length ? { from: (rev.photos?.before || []).length, to: editingRevisionPhotos.before.length } : null,
              photos_after_count: (rev.photos?.after || []).length !== editingRevisionPhotos.after.length ? { from: (rev.photos?.after || []).length, to: editingRevisionPhotos.after.length } : null
            }
          };
          
          return {
            ...rev,
            revision_date: editingRevision.date,
            category: editingRevision.category,
            description: editingRevision.description,
            photos: {
              before: editingRevisionPhotos.before,
              after: editingRevisionPhotos.after
            },
            edit_history: [...editHistory, newEditEntry],
            last_edited_at: new Date().toISOString(),
            last_edited_by: userRole
          };
        }
        return rev;
      });
      
      // Backend'e gönder (personnel_id middleware'de gölge admin kontrolü için)
      const q = personnelId ? `?personnel_id=${encodeURIComponent(personnelId)}` : "";
      const response = await axios.put(`${API}/api/parks/${parkId}${q}`, {
        completed_revisions_history: updatedHistory
      });
      
      if (response.data) {
        // Local state'i güncelle
        const updatedPark = response.data;
        const updatedHistoryFromResponse = updatedPark.completed_revisions_history || [];
        
        // parkRevisions state'ini güncelle
        setParkRevisions(prev => prev.map(rev => {
          if (rev.revision_id === editingRevisionId) {
            const matchingRev = updatedHistoryFromResponse.find(r => r.revision_id === editingRevisionId);
            if (matchingRev) {
              return {
                ...rev,
                ...matchingRev,
                revision_date: matchingRev.revision_date || rev.revision_date
              };
            }
          }
          return rev;
        }));
        
        toast.success("Revize kaydı güncellendi");
        setEditingRevisionId(null);
        setEditingRevision(null);
        setEditingRevisionPhotos({ before: [], after: [] });
        
        // Parks listesini yenile
        window.dispatchEvent(new CustomEvent('refreshParks'));
      }
    } catch (error) {
      console.error("Save edited revision error:", error);
      console.error("Error response:", error.response);
      const errorMessage = error.response?.data?.detail || error.message || "Revize kaydı güncellenirken hata oluştu";
      toast.error(`Hata: ${errorMessage}`);
    } finally {
      setUploadingEditPhotos(false);
    }
  };

  // Düzenleme sırasında fotoğraf yükleme
  const handleUploadEditPhoto = async (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const currentPhotos = editingRevisionPhotos[type] || [];
    if (files.length + currentPhotos.length > 10) {
      toast.error("Maksimum 10 fotoğraf yükleyebilirsiniz");
      return;
    }
    
    setUploadingEditPhotos(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/api/upload-revision-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return response.data.url;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      setEditingRevisionPhotos(prev => ({
        ...prev,
        [type]: [...(prev[type] || []), ...uploadedUrls]
      }));
      
      toast.success(`${uploadedUrls.length} fotoğraf yüklendi`);
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error("Fotoğraf yüklenirken hata oluştu");
    } finally {
      setUploadingEditPhotos(false);
    }
  };

  // Düzenleme sırasında fotoğraf silme
  const handleRemoveEditPhoto = (type, index) => {
    setEditingRevisionPhotos(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };



  // Tarih formatını "gün ay yıl" şeklinde göster
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                     'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Lightbox açma fonksiyonu
  const openLightbox = (photos, startIndex = 0) => {
    console.log("openLightbox called with:", photos, startIndex);
    if (!photos || photos.length === 0) {
      console.error("No photos to show in lightbox");
      return;
    }
    setLightboxPhotos(photos);
    setLightboxCurrentIndex(startIndex);
    setLightboxOpen(true);
  };

  // Lightbox'da sonraki fotoğraf
  const nextPhoto = () => {
    setLightboxCurrentIndex((prev) => (prev + 1) % lightboxPhotos.length);
  };

  // Lightbox'da önceki fotoğraf
  const prevPhoto = () => {
    setLightboxCurrentIndex((prev) => (prev - 1 + lightboxPhotos.length) % lightboxPhotos.length);
  };

  // Klavye ile gezinme
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setLightboxCurrentIndex((prev) => (prev + 1) % lightboxPhotos.length);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setLightboxCurrentIndex((prev) => (prev - 1 + lightboxPhotos.length) % lightboxPhotos.length);
      } else if (e.key === 'Escape') {
        setLightboxOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxPhotos.length]);

  return (
    <div className="space-y-6">
      {/* Yeni Revize Ekleme Formu */}
      {!isFormReadOnly && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Yeni Revize Kaydı
            </CardTitle>
            <CardDescription>Parkta yapılan değişiklikleri kaydedin</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <DatePicker
                  id="revision-date"
                  label="Revize Tarihi"
                  required
                  value={newRevision.date}
                  onChange={(value) => setNewRevision(prev => ({...prev, date: value}))}
                />
              </div>
              
              <div>
                <Label htmlFor="revision-category">Revize Türü *</Label>
                <SearchableSelect
                  value={newRevision.category}
                  onChange={(value) => setNewRevision(prev => ({...prev, category: value}))}
                  showAllOption={false}
                  placeholder="Revize türü seçin"
                  searchPlaceholder="Revize türü ara..."
                  options={REVISION_TYPES.map(cat => ({ id: cat, name: cat }))}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="revision-description">Açıklama/Not</Label>
              <Textarea
                id="revision-description"
                value={newRevision.description}
                onChange={(e) => setNewRevision(prev => ({...prev, description: e.target.value}))}
                className="min-h-[100px] resize-y"
                placeholder="Örn: Eski spor aletleri kaldırıldı, yeni spor aletleri takıldı..."
              />
            </div>
            
            <div className="space-y-4">
              <Label>Fotoğraflar (Opsiyonel)</Label>
              
              {/* Eski Fotoğraflar */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-sm font-semibold mb-2 block">Eskisi</Label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handlePhotoUpload(e, 'before')}
                    disabled={uploadingPhotos || (newRevision.photos.before?.length || 0) >= 10}
                    className="hidden"
                    id="revision-photos-before-upload"
                  />
                  <label
                    htmlFor="revision-photos-before-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer ${
                      uploadingPhotos || (newRevision.photos.before?.length || 0) >= 10
                        ? 'bg-gray-100 cursor-not-allowed'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <Camera className="h-4 w-4" />
                    {uploadingPhotos ? 'Yükleniyor...' : 'Eski Fotoğraf Ekle'}
                  </label>
                  
                  {newRevision.photos.before && newRevision.photos.before.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      {newRevision.photos.before.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={mediaUrl(API, photo)}
                            alt={`Eski fotoğraf ${index + 1}`}
                            className="w-full h-24 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto('before', index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Yeni Fotoğraflar */}
              <div className="border rounded-lg p-4 bg-green-50">
                <Label className="text-sm font-semibold mb-2 block">Yenisi</Label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handlePhotoUpload(e, 'after')}
                    disabled={uploadingPhotos || (newRevision.photos.after?.length || 0) >= 10}
                    className="hidden"
                    id="revision-photos-after-upload"
                  />
                  <label
                    htmlFor="revision-photos-after-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer ${
                      uploadingPhotos || (newRevision.photos.after?.length || 0) >= 10
                        ? 'bg-gray-100 cursor-not-allowed'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <Camera className="h-4 w-4" />
                    {uploadingPhotos ? 'Yükleniyor...' : 'Yeni Fotoğraf Ekle'}
                  </label>
                  
                  {newRevision.photos.after && newRevision.photos.after.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      {newRevision.photos.after.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={mediaUrl(API, photo)}
                            alt={`Yeni fotoğraf ${index + 1}`}
                            className="w-full h-24 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto('after', index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <Button 
              onClick={handleSaveRevision}
              disabled={!newRevision.date || !newRevision.category || uploadingPhotos}
              className="w-full"
            >
              <FileText className="h-4 w-4 mr-2" />
              Revize Kaydını Ekle
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Revize Geçmişi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Revize Geçmişi ({parkRevisions.length})
          </CardTitle>
          <CardDescription>Bu parkta yapılan tüm revizeler</CardDescription>
        </CardHeader>
        <CardContent>
          {parkRevisions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Henüz revize kaydı bulunmamaktadır</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {parkRevisions.map((rev, idx) => (
                <div 
                  key={rev.revision_id || rev.id || idx} 
                  className="p-5 border rounded-xl bg-white shadow-sm hover:shadow-md transition-all border-gray-200"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Başlık ve Tarih */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <Calendar className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-blue-100 text-blue-800 border-blue-300 font-medium px-3 py-1">
                              {rev.category || rev.title}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="font-medium">
                              {rev.revision_date 
                                ? formatDate(rev.revision_date)
                                : rev.completed_at 
                                  ? formatDate(rev.completed_at)
                                  : 'Tarih belirtilmemiş'
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Açıklama */}
                      {rev.description && (
                        <div className="ml-11 mb-3">
                          <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-100">
                            {rev.description}
                          </p>
                        </div>
                      )}
                      
                      {/* Düzenleme Geçmişi */}
                      {rev.edit_history && rev.edit_history.length > 0 && (
                        <div className="ml-11 mb-3">
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="h-4 w-4 text-amber-700" />
                              <Label className="text-xs font-semibold text-amber-900">Düzenleme Geçmişi</Label>
                            </div>
                            <div className="space-y-2">
                              {rev.edit_history.map((edit, editIdx) => (
                                <div key={editIdx} className="text-xs text-amber-800 bg-white/50 p-2 rounded border border-amber-100">
                                  <div className="font-medium">
                                    {formatDate(edit.edited_at)} - {edit.edited_by === 'mudur' ? 'Müdür' : edit.edited_by === 'mudur_yardimcisi' ? 'Müdür Yardımcısı' : edit.edited_by} tarafından düzenlendi
                                  </div>
                                  {Object.entries(edit.changes || {}).some(([_, change]) => change !== null) && (
                                    <div className="mt-1 text-amber-700">
                                      {edit.changes.date && (
                                        <div>• Tarih: {formatDate(edit.changes.date.from)} → {formatDate(edit.changes.date.to)}</div>
                                      )}
                                      {edit.changes.category && (
                                        <div>• Kategori: {edit.changes.category.from} → {edit.changes.category.to}</div>
                                      )}
                                      {edit.changes.description && (
                                        <div>• Açıklama güncellendi</div>
                                      )}
                                      {edit.changes.photos_before_count && (
                                        <div>• Eski fotoğraflar: {edit.changes.photos_before_count.from} → {edit.changes.photos_before_count.to}</div>
                                      )}
                                      {edit.changes.photos_after_count && (
                                        <div>• Yeni fotoğraflar: {edit.changes.photos_after_count.from} → {edit.changes.photos_after_count.to}</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Fotoğraflar - Eskisi ve Yenisi olarak ayrı göster */}
                      {rev.photos && (
                        (() => {
                          // Eski format (array) veya yeni format (object) kontrolü
                          const photosBefore = Array.isArray(rev.photos) 
                            ? [] 
                            : (rev.photos.before || []);
                          const photosAfter = Array.isArray(rev.photos) 
                            ? rev.photos 
                            : (rev.photos.after || []);
                          
                          if (photosBefore.length === 0 && photosAfter.length === 0) return null;
                          
                          return (
                            <div className="ml-11 mt-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Eskisi - Sol Taraf */}
                                {photosBefore.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                      <Label className="text-sm font-semibold text-gray-700">Eskisi</Label>
                                      <span className="text-xs text-gray-500">({photosBefore.length} fotoğraf)</span>
                                    </div>
                                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                      {photosBefore.map((photo, photoIdx) => {
                                        return (
                                          <div 
                                            key={photoIdx} 
                                            className="relative group cursor-pointer"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              console.log("Eski fotoğraf tıklandı, index:", photoIdx);
                                              console.log("Eski fotoğraflar:", photosBefore);
                                              // Sadece eski fotoğrafları aç
                                              openLightbox(photosBefore, photoIdx);
                                            }}
                                          >
                                            <img
                                              src={mediaUrl(API, photo)}
                                              alt={`Eski fotoğraf ${photoIdx + 1}`}
                                              className="w-full h-16 object-cover rounded border border-gray-200 hover:border-blue-400 hover:opacity-90 transition-all pointer-events-none"
                                            />
                                            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded transition-all flex items-center justify-center pointer-events-none">
                                              <Camera className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Yenisi - Sağ Taraf */}
                                {photosAfter.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                      <Label className="text-sm font-semibold text-green-700">Yenisi</Label>
                                      <span className="text-xs text-gray-500">({photosAfter.length} fotoğraf)</span>
                                    </div>
                                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                      {photosAfter.map((photo, photoIdx) => {
                                        return (
                                          <div 
                                            key={photoIdx} 
                                            className="relative group cursor-pointer"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              console.log("Yeni fotoğraf tıklandı, index:", photoIdx);
                                              console.log("Yeni fotoğraflar:", photosAfter);
                                              // Sadece yeni fotoğrafları aç
                                              openLightbox(photosAfter, photoIdx);
                                            }}
                                          >
                                            <img
                                              src={mediaUrl(API, photo)}
                                              alt={`Yeni fotoğraf ${photoIdx + 1}`}
                                              className="w-full h-16 object-cover rounded border border-green-200 hover:border-green-400 hover:opacity-90 transition-all pointer-events-none"
                                            />
                                            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded transition-all flex items-center justify-center pointer-events-none">
                                              <Camera className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()
                      )}
                    </div>
                    
                    {!isFormReadOnly && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Revize Geçmişini Düzenle Butonu - Sadece müdür ve müdür yardımcısı için */}
                        {canEditRevisions && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                            onClick={() => handleEditRevision(rev)}
                            title="Revize Geçmişini Düzenle"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            <span className="text-xs">Düzenle</span>
                          </Button>
                        )}
                        
                        {/* Sil Butonu - Sadece müdür ve müdür yardımcısı için */}
                        {canEditRevisions && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                            onClick={() => {
                              if (rev.isActive && onDeleteRevision) {
                                onDeleteRevision(rev.id);
                              } else if (!rev.isActive && onDeleteCompletedRevisionHistory) {
                                openHistoryDeleteDialog(rev);
                              }
                            }}
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revize Düzenleme Dialog'u */}
      <DraggableModal
        isOpen={editingRevisionId !== null && editingRevision !== null}
        onClose={() => {
          setEditingRevisionId(null);
          setEditingRevision(null);
          setEditingRevisionPhotos({ before: [], after: [] });
        }}
        title="Revize Geçmişini Düzenle"
        subtitle="Revize kaydını düzenleyebilirsiniz. Yapılan değişiklikler kaydedilecektir."
        maxWidth="max-w-4xl"
      >
          <div className="space-y-6">
            
            {editingRevision && (
              <>
                {/* Tarih */}
                <div>
                  <DatePicker
                    id="edit-revision-date"
                    label="Revize Tarihi"
                    required
                    value={editingRevision.date}
                    onChange={(value) => setEditingRevision(prev => ({ ...prev, date: value }))}
                  />
                </div>
                
                {/* Kategori */}
                <div>
                  <Label htmlFor="edit-revision-category">Revize Türü *</Label>
                  <SearchableSelect
                    value={editingRevision.category}
                    onChange={(value) => setEditingRevision(prev => ({ ...prev, category: value }))}
                    showAllOption={false}
                    placeholder="Revize türü seçin"
                    searchPlaceholder="Revize türü ara..."
                    className="mt-1"
                    options={REVISION_TYPES.map((cat) => ({ id: cat, name: cat }))}
                  />
                </div>
                
                {/* Açıklama */}
                <div>
                  <Label htmlFor="edit-revision-description">Açıklama</Label>
                  <Textarea
                    id="edit-revision-description"
                    value={editingRevision.description}
                    onChange={(e) => setEditingRevision(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1"
                    rows={4}
                    placeholder="Revize açıklaması..."
                  />
                </div>
                
                {/* Fotoğraflar - Eskisi */}
                <div>
                  <Label>Eski Fotoğraflar</Label>
                  <div className="mt-2 space-y-3">
                    {/* Mevcut Fotoğraflar */}
                    {editingRevisionPhotos.before.length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {editingRevisionPhotos.before.map((photo, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={mediaUrl(API, photo)}
                              alt={`Eski fotoğraf ${index + 1}`}
                              className="w-full h-20 object-cover rounded border border-gray-200"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute top-0 right-0 h-6 w-6 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-md z-10"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveEditPhoto('before', index);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Fotoğraf Yükleme */}
                    <div>
                      <Label htmlFor="edit-photo-before" className="text-sm font-medium text-gray-700 mb-2 block">
                        Dosyaları Seç (Eski)
                      </Label>
                      <div className="relative">
                        <Input
                          id="edit-photo-before"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handleUploadEditPhoto(e, 'before')}
                          disabled={uploadingEditPhotos || editingRevisionPhotos.before.length >= 10}
                          className="hidden"
                        />
                        <label
                          htmlFor="edit-photo-before"
                          className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer ${
                            (uploadingEditPhotos || editingRevisionPhotos.before.length >= 10) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <Camera className="h-4 w-4 mr-2" />
                          Fotoğraf Seç
                        </label>
                        <span className="ml-3 text-sm text-gray-500">
                          {uploadingEditPhotos ? 'Yükleniyor...' : editingRevisionPhotos.before.length >= 10 ? 'Maksimum fotoğraf sayısına ulaşıldı' : 'Dosya seçilmedi'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {editingRevisionPhotos.before.length}/10 fotoğraf (Maksimum 10)
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Fotoğraflar - Yenisi */}
                <div>
                  <Label>Yeni Fotoğraflar</Label>
                  <div className="mt-2 space-y-3">
                    {/* Mevcut Fotoğraflar */}
                    {editingRevisionPhotos.after.length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {editingRevisionPhotos.after.map((photo, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={mediaUrl(API, photo)}
                              alt={`Yeni fotoğraf ${index + 1}`}
                              className="w-full h-20 object-cover rounded border border-green-200"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute top-0 right-0 h-6 w-6 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-md z-10"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveEditPhoto('after', index);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Fotoğraf Yükleme */}
                    <div>
                      <Label htmlFor="edit-photo-after" className="text-sm font-medium text-gray-700 mb-2 block">
                        Dosyaları Seç (Yeni)
                      </Label>
                      <div className="relative">
                        <Input
                          id="edit-photo-after"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handleUploadEditPhoto(e, 'after')}
                          disabled={uploadingEditPhotos || editingRevisionPhotos.after.length >= 10}
                          className="hidden"
                        />
                        <label
                          htmlFor="edit-photo-after"
                          className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer ${
                            (uploadingEditPhotos || editingRevisionPhotos.after.length >= 10) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <Camera className="h-4 w-4 mr-2" />
                          Fotoğraf Seç
                        </label>
                        <span className="ml-3 text-sm text-gray-500">
                          {uploadingEditPhotos ? 'Yükleniyor...' : editingRevisionPhotos.after.length >= 10 ? 'Maksimum fotoğraf sayısına ulaşıldı' : 'Dosya seçilmedi'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {editingRevisionPhotos.after.length}/10 fotoğraf (Maksimum 10)
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Butonlar */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingRevisionId(null);
                      setEditingRevision(null);
                      setEditingRevisionPhotos({ before: [], after: [] });
                    }}
                    disabled={uploadingEditPhotos}
                  >
                    İptal
                  </Button>
                  <Button
                    onClick={handleSaveEditedRevision}
                    disabled={uploadingEditPhotos || !editingRevision.date || !editingRevision.category}
                  >
                    {uploadingEditPhotos ? "Kaydediliyor..." : "Kaydet"}
                  </Button>
                </div>
              </>
            )}
          </div>
      </DraggableModal>

      {/* Revize Geçmişi Silme Onayı (Uygulama içi 2 adım) */}
      <DraggableModal
        isOpen={historyDeleteDialog.open}
        onClose={closeHistoryDeleteDialog}
        title={historyDeleteDialog.step === 1 ? "Geçmiş Revize Kaydını Sil" : "Son Onay"}
        subtitle={historyDeleteDialog.category ? `${parkName || "Park"} - ${historyDeleteDialog.category}` : (parkName || "")}
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          {historyDeleteDialog.step === 1 ? (
            <p className="text-sm text-gray-700">
              Bu geçmiş revize kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
          ) : (
            <p className="text-sm text-red-700 font-medium">
              Son onay: Bu kayıt kalıcı olarak silinecek ve tüm panellerden kaldırılacak.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={closeHistoryDeleteDialog}>
              İptal
            </Button>
            {historyDeleteDialog.step === 1 ? (
              <Button
                variant="destructive"
                onClick={() => setHistoryDeleteDialog((prev) => ({ ...prev, step: 2 }))}
              >
                Devam Et
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleConfirmHistoryDelete}>
                Evet, Kalıcı Sil
              </Button>
            )}
          </div>
        </div>
      </DraggableModal>

      {/* Fotoğraf Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={(open) => {
        console.log("Dialog onOpenChange:", open);
        setLightboxOpen(open);
      }}>
        <DialogContent className="max-w-6xl w-full p-0 bg-black/95 border-none" onInteractOutside={(e) => e.preventDefault()}>
          <div className="relative w-full h-[90vh] flex items-center justify-center">
            {/* Önceki Fotoğraf Butonu */}
            {lightboxPhotos.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 z-10 h-12 w-12 bg-black/50 hover:bg-black/70 text-white rounded-full border border-white/20"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  prevPhoto();
                }}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            )}

            {/* Fotoğraf */}
            {lightboxPhotos.length > 0 && lightboxPhotos[lightboxCurrentIndex] && (
              <div className="flex items-center justify-center w-full h-full p-8">
                <img
                  src={mediaUrl(API, lightboxPhotos[lightboxCurrentIndex])}
                  alt={`Fotoğraf ${lightboxCurrentIndex + 1}`}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  onError={(e) => {
                    console.error("Image load error:", e.target.src);
                  }}
                />
              </div>
            )}

            {/* Sonraki Fotoğraf Butonu */}
            {lightboxPhotos.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 z-10 h-12 w-12 bg-black/50 hover:bg-black/70 text-white rounded-full border border-white/20"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  nextPhoto();
                }}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            )}

            {/* Fotoğraf Sayacı */}
            {lightboxPhotos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                {lightboxCurrentIndex + 1} / {lightboxPhotos.length}
              </div>
            )}

            {/* Kapatma Butonu */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 h-10 w-10 bg-black/50 hover:bg-black/70 text-white rounded-full border border-white/20"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setLightboxOpen(false);
              }}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function ParksTab({
  userRole,
  personnelId,
  parks,
  personnel,
  regions,
  loadingParks,
  searchTerm,
  setSearchTerm,
  filterPersonnel,
  setFilterPersonnel,
  filterRegion,
  setFilterRegion,
  parksViewMode,
  setParksViewMode,
  parksSortOrder,
  setParksSortOrder,
  currentPage,
  setCurrentPage,
  handleParkEdit,
  handleParkDelete,
  setIsParkDialogOpen,
  getAllSubordinates,
  getRoleBadge,
  // Additional props needed
  displayedParks,
  // Faz 2 — paginated lightweight cards
  parkCards = [],
  parkCardsTotal = 0,
  parkCardsHasMore = false,
  parkCardsLoading = false,
  parkCardsLoadingMore = false,
  loadMoreParkCards = () => {},
  fetchParkDetail = null,
  parkAggregates = null,
  parkBoundaries = [],
  isParkDialogOpen,
  parkForm,
  setParkForm,
  isEditingPark,
  isFormReadOnly = false,
  setIsFormReadOnly,
  resetParkForm,
  handleParkSubmit,
  isGeocodingAddress,
  setIsGeocodingAddress,
  showMap,
  setShowMap,
  drawingMode,
  setDrawingMode,
  drawnBoundaries,
  setDrawnBoundaries,
  setQuickAddContext,
  setIsQuickRegionDialogOpen,
  setParkToDelete,
  API,
  loading,
  isParkSaving = false,
  revisions = [],
  highlightedParkId = null,
  onDeleteRevision = null, // Revize silme fonksiyonu
  onDeleteCompletedRevisionHistory = null, // Geçmiş revize silme fonksiyonu
  activeSubstitutions = [], // Aktif vekaletler
  quickParkFilter = null
}) {
  const parkSubmitLoading = Boolean(isParkSaving || loading);
  const [mapLayer, setMapLayer] = useMapLayer("street"); // Harita / Uydu (görünüm haritası)
  // Harita yakınlık katmanları. Taban katman (parklar) kalıcı DEĞİL: haritaya
  // her girişte işaretli gelir. Ek katmanlar (pasif alan / protokollü park)
  // tarayıcıda hatırlanır.
  const [showParksLayer, setShowParksLayer] = useState(true);
  const [showPasifLayer, setShowPasifLayer] = usePersistedFlag('parkMap.showPasif', false);
  const [showProtokolLayer, setShowProtokolLayer] = usePersistedFlag('parkMap.showProtokol', false);
  const [linkLine, setLinkLine] = useState(null);   // [[parkLat,parkLng],[refLat,refLng]]
  const [linkInfo, setLinkInfo] = useState(null);   // { refName, distance }

  // Haritada gösterilebilir (koordinatlı) parklar.
  const mapParks = useMemo(
    () => (Array.isArray(displayedParks) ? displayedParks : [])
      .map(p => ({ ...p, _lat: toCoord(p.latitude), _lng: toCoord(p.longitude) }))
      .filter(p => p._lat !== null && p._lng !== null),
    [displayedParks]
  );

  const { points: nearbyPoints, loading: loadingNearby } = useNearbyPoints(
    API,
    NEARBY_LAYER_KEYS,
    parksViewMode === 'map'
  );

  // Harita her açıldığında taban katmanı geri aç.
  useEffect(() => {
    if (parksViewMode === 'map') setShowParksLayer(true);
  }, [parksViewMode]);

  const pasifPoints = useMemo(() => nearbyPoints.pasif || [], [nearbyPoints.pasif]);
  const protokolPoints = useMemo(() => nearbyPoints.protokol || [], [nearbyPoints.protokol]);

  // Mesafe hesabı tüm referanslar üzerinden; katman kapalıyken de "en yakın" çalışır.
  const referencePoints = useMemo(
    () => [...pasifPoints, ...protokolPoints],
    [pasifPoints, protokolPoints]
  );

  // Katman seçimine göre haritada çizilecek referanslar.
  const visibleReferences = useMemo(() => {
    const rows = [];
    if (showPasifLayer) rows.push(...pasifPoints);
    if (showProtokolLayer) rows.push(...protokolPoints);
    return rows;
  }, [showPasifLayer, showProtokolLayer, pasifPoints, protokolPoints]);

  // Her park için en yakın 3 pasif alan / protokollü park (kuş uçuşu).
  const nearestOrigins = useMemo(
    () => mapParks.map(p => ({ id: p.id, kind: 'park', lat: p._lat, lng: p._lng })),
    [mapParks]
  );
  const nearestByParkId = useNearest(nearestOrigins, referencePoints);

  // Otomatik çerçeveleme: bağlantı çizgisi varsa iki noktaya, yoksa tüm parklara.
  const mapFitBounds = linkLine || mapParks.map(p => [p._lat, p._lng]);
  const mapFitKey = linkLine
    ? `link:${JSON.stringify(linkLine)}`
    : `parks:${mapParks.map(p => p.id).join('|')}`;

  // "En yakın" listesinden bir referansa tıklanınca: katmanı aç, çizgiyi kur.
  const focusReference = (park, ref) => {
    if (ref.kind === 'pasif') setShowPasifLayer(true);
    else setShowProtokolLayer(true);
    setLinkLine([[park._lat, park._lng], [ref.lat, ref.lng]]);
    setLinkInfo({ refName: ref.name, distance: ref.distance });
  };

  const clearLink = () => {
    setLinkLine(null);
    setLinkInfo(null);
  };

  // Referans popup'larında bölge adı.
  const getRegionNameForMap = useCallback(
    (regionId) => regions.find(r => r.id === regionId)?.name || '-',
    [regions]
  );

  const [formMapLayer, setFormMapLayer] = useMapLayer("street"); // Harita / Uydu (konum/çizim haritası)
  const [formSearchCenter, setFormSearchCenter] = useState(null); // Adres aramada bulunan konum

  // Sprint 2 F3: Dinamik yetki context'i (rol flag'leri + permission)
  const {
    has,
    isAdmin,
    isMudur,
    isMudurYardimcisi,
    isBolgeMuhendisi,
    isFormen,
    isParkGorevlisi,
    isGolgeAdmin,
  } = useRoleFlags(userRole);

  // Park filtre granular yetkileri (Sprint 4 F1 — dinamik yetkiye geçiş)
  const canSearch = has("park.filtre.arama");
  const canFilterBolge = has("park.filtre.bolge");
  const canFilterMY = has("park.filtre.mudur_yardimcisi");
  const canFilterBM = has("park.filtre.bolge_muhendisi");
  const canFilterFormen = has("park.filtre.formen");
  const canFilterPG = has("park.filtre.park_gorevlisi");
  const hasAnyFilter = canFilterBolge || canFilterMY || canFilterBM || canFilterFormen || canFilterPG;

  // Park aksiyon yetkileri (UI buton görünürlüğü)
  const canEditPark = has("park.duzenle");
  const canCreatePark = has("park.olustur");
  const canDeletePark = has("park.sil");

  // Konum düzenleme modu state'i - varsayılan olarak kapalı
  const [locationEditMode, setLocationEditMode] = useState(false);
  const [parkSearchInput, setParkSearchInput] = useState(searchTerm || "");
  const deferredParkSearchInput = useDeferredValue(parkSearchInput);

  useEffect(() => {
    setParkSearchInput(searchTerm || "");
  }, [searchTerm]);

  useEffect(() => {
    // Deferred değer henüz son input'a yetişmediyse push etme — yoksa
    // dışarıdan gelen reset (örn. "Filtreleri Temizle") eski değerle ezilir.
    if (parkSearchInput !== deferredParkSearchInput) return;
    if ((searchTerm || "") !== (deferredParkSearchInput || "")) {
      setSearchTerm(deferredParkSearchInput || "");
    }
  }, [parkSearchInput, deferredParkSearchInput, searchTerm, setSearchTerm]);

  // Park görevlisi izindeyken, parkına bakan vekil bilgisini al
  const getCoverageSubstitutionForPark = (parkId, originalPersonnelId) => {
    const sub = activeSubstitutions.find(sub =>
      sub.original_personnel_id === originalPersonnelId &&
      sub.parks?.some(p => (typeof p === "string" ? p : (p?.id || p?.park_id)) === parkId)
    );
    if (!sub) return sub;
    // Çöktürülmüş vekalet kaydı tüm parkları TEK kayıtta birleştirir; üst-seviye
    // substitute_personnel_name yalnız "ilk dolu vekil"dir. Bu parkın gerçek
    // vekilini per-park kırılımdan (substitute_breakdown) çöz; yoksa üst-seviye
    // alanı koru (tek-vekil senaryo).
    const breakdown = Array.isArray(sub.substitute_breakdown) ? sub.substitute_breakdown : [];
    const entry = breakdown.find(b => (typeof b === "string" ? b : (b?.id || b?.park_id)) === parkId);
    if (entry?.substitute_personnel_name) {
      return { ...sub, substitute_personnel_name: entry.substitute_personnel_name };
    }
    return sub;
  };

  // Faz 2 — Infinite scroll sentinel
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !parkCardsHasMore || parkCardsLoadingMore || parkCardsLoading) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreParkCards();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [parkCardsHasMore, parkCardsLoadingMore, parkCardsLoading, loadMoreParkCards, parkCards.length]);

  // Park düzenleme — dirty tracking + edit mode toggle (yeni unified akış)
  // Modal her zaman read-only başlar (App.js handleParkEdit), kullanıcı "Düzenle" ile editable'a geçer.
  const [parkFormSnapshot, setParkFormSnapshot] = useState(null);
  const isParkFormDirty = useMemo(() => {
    if (!parkFormSnapshot) return false;
    try {
      return JSON.stringify(parkForm) !== JSON.stringify(parkFormSnapshot);
    } catch {
      return false;
    }
  }, [parkForm, parkFormSnapshot]);

  // Düzenle butonuna basınca: snapshot al, editable moda geç
  const handleEnterEditMode = useCallback(() => {
    try {
      setParkFormSnapshot(JSON.parse(JSON.stringify(parkForm)));
    } catch {
      setParkFormSnapshot(parkForm);
    }
    // setIsFormReadOnly App.js'den prop olarak gelmiyor — modal içinde state'i ParksTab'tan
    // değiştiremeyiz. Onun yerine App.js'e callback gönderelim.
    if (typeof setIsFormReadOnly === 'function') {
      setIsFormReadOnly(false);
    }
    // Form düzenlenebilir olunca harita da düzenlenebilir moda geçsin
    setLocationEditMode(true);
  }, [parkForm]);

  // Düzenlemeyi Bitir: dirty ise onay sor, sonra read-only'ye dön
  const handleExitEditMode = useCallback(() => {
    if (isParkFormDirty) {
      const ok = window.confirm("Kaydedilmemiş değişiklikleriniz var.\n\nKaydetmeden vazgeçilsin mi?");
      if (!ok) return;
      // Orjinal değerlere dön
      if (parkFormSnapshot && typeof setParkForm === 'function') {
        setParkForm(parkFormSnapshot);
      }
    }
    setParkFormSnapshot(null);
    if (typeof setIsFormReadOnly === 'function') {
      setIsFormReadOnly(true);
    }
    // Form view-only'ye dönerken haritayı da kilitle ve çizim modunu kapat
    setLocationEditMode(false);
    if (typeof setDrawingMode === 'function') {
      setDrawingMode(false);
    }
  }, [isParkFormDirty, parkFormSnapshot, setParkForm, setDrawingMode]);

  // X butonu / overlay'e tıklayınca dirty check
  const handleParkModalClose = useCallback(() => {
    if (!isFormReadOnly && isParkFormDirty) {
      const ok = window.confirm("Kaydedilmemiş değişiklikleriniz var.\n\nKaydetmeden kapatılsın mı?");
      if (!ok) return;
    }
    setParkFormSnapshot(null);
    if (typeof setIsParkDialogOpen === 'function') {
      setIsParkDialogOpen(false);
    }
    if (typeof resetParkForm === 'function') {
      resetParkForm();
    }
  }, [isFormReadOnly, isParkFormDirty, setIsParkDialogOpen, resetParkForm]);

  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800
  );

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const instantFilteredDisplayedParks = useMemo(() => {
    // Türkçe-aware + çoklu kelime: App.js'deki ana filtreyle aynı mantık.
    // Aksi halde "beysu cum" gibi sorgular ana filtreden geçse bile burada elenir.
    const normalize = normalizeSearchText;
    const queryNormalized = normalize(deferredParkSearchInput);
    const tokens = queryNormalized ? queryNormalized.split(/\s+/) : [];
    const list = Array.isArray(displayedParks) ? displayedParks : [];
    const searched = tokens.length === 0 ? list : list.filter((park) => {
      const haystack = [park?.name, park?.neighborhood, park?.address]
        .filter(Boolean)
        .map(normalize)
        .join(" ");
      return tokens.every(tok => haystack.includes(tok));
    });

    // PG ana sayfa kartlarından gelen hızlı filtre
    if (!isParkGorevlisi || !quickParkFilter?.kind || quickParkFilter.kind === "all") {
      return searched;
    }

    return searched.filter((park) => {
      if (!park) return false;
      if (quickParkFilter.kind === "grass_positive") {
        return Number(park.grass_area || 0) > 0;
      }
      if (quickParkFilter.kind === "inventory" && quickParkFilter.key) {
        return Number(park[quickParkFilter.key] || 0) > 0;
      }
      if (quickParkFilter.kind === "feature" && quickParkFilter.key) {
        return Boolean(park[quickParkFilter.key]);
      }
      if (quickParkFilter.kind === "irrigation") {
        const value = String(park.irrigation_system || "").toLowerCase();
        const target = String(quickParkFilter.value || "").toLowerCase();
        if (target === "yok") return !value || value === "yok";
        return value === target;
      }
      return true;
    });
  }, [displayedParks, deferredParkSearchInput, userRole, quickParkFilter]);

  const sortedDisplayedParks = useMemo(() => {
    if (parksSortOrder === "alphabetical") {
      return [...instantFilteredDisplayedParks].sort((a, b) => a.name.localeCompare(b.name, "tr"));
    }
    return instantFilteredDisplayedParks;
  }, [instantFilteredDisplayedParks, parksSortOrder]);

  const tableListHeight = Math.max(320, Math.min(600, viewportHeight - 300));
  const [parkFilterMY, setParkFilterMY] = useState("all");
  const [parkFilterBM, setParkFilterBM] = useState("all");
  const [parkFilterFormen, setParkFilterFormen] = useState("all");
  const [parkFilterPG, setParkFilterPG] = useState("all");

  const matchesRegionFilterForPerson = useCallback((person, selectedRegion) => {
    if (selectedRegion === "all") return true;
    const regionIds = Array.isArray(person.region_ids) ? person.region_ids : [];
    const hasRegion = Boolean(person.region_id) || regionIds.length > 0;
    if (selectedRegion === "assigned") return hasRegion;
    if (selectedRegion === "none") return !hasRegion;
    return person.region_id === selectedRegion || regionIds.includes(selectedRegion);
  }, []);

  const personnelById = useMemo(
    () => new Map((personnel || []).map((p) => [p.id, p])),
    [personnel]
  );

  const sortByName = useCallback(
    (list) => [...list].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "tr")),
    []
  );

  const myOptions = useMemo(() => {
    if (!isMudur) return [];
    return sortByName(
      personnel.filter(
        (p) => p.role === "mudur_yardimcisi" && matchesRegionFilterForPerson(p, filterRegion)
      )
    );
  }, [userRole, personnel, filterRegion, matchesRegionFilterForPerson, sortByName]);

  const bmOptions = useMemo(() => {
    let list = personnel.filter((p) => p.role === "bolge_muhendisi");

    if (isMudur && parkFilterMY !== "all") {
      list = list.filter((p) => p.mudur_yardimcisi_id === parkFilterMY);
    } else if (isMudurYardimcisi) {
      list = list.filter((p) => p.mudur_yardimcisi_id === personnelId);
    } else if (isBolgeMuhendisi) {
      list = list.filter((p) => p.id === personnelId);
    }

    list = list.filter((p) => matchesRegionFilterForPerson(p, filterRegion));
    return sortByName(list);
  }, [userRole, personnel, personnelId, parkFilterMY, filterRegion, matchesRegionFilterForPerson, sortByName]);

  const formenOptions = useMemo(() => {
    let list = personnel.filter((p) => p.role === "formen");

    if (parkFilterBM !== "all") {
      list = list.filter((p) => p.bolge_muhendisi_id === parkFilterBM);
    } else if (isMudur && parkFilterMY !== "all") {
      list = list.filter((p) => {
        const bm = personnelById.get(p.bolge_muhendisi_id);
        return bm?.mudur_yardimcisi_id === parkFilterMY;
      });
    } else if (isMudurYardimcisi) {
      list = list.filter((p) => {
        const bm = personnelById.get(p.bolge_muhendisi_id);
        return bm?.mudur_yardimcisi_id === personnelId;
      });
    } else if (isBolgeMuhendisi) {
      list = list.filter((p) => p.bolge_muhendisi_id === personnelId);
    }

    list = list.filter((p) => matchesRegionFilterForPerson(p, filterRegion));
    return sortByName(list);
  }, [
    userRole,
    personnel,
    personnelId,
    parkFilterBM,
    parkFilterMY,
    personnelById,
    filterRegion,
    matchesRegionFilterForPerson,
    sortByName,
  ]);

  const pgOptions = useMemo(() => {
    let list = personnel.filter((p) => p.role === "park_gorevlisi");

    if (parkFilterFormen !== "all") {
      list = list.filter((p) => p.formen_id === parkFilterFormen);
    } else if (parkFilterBM !== "all") {
      list = list.filter((p) => {
        const formen = personnelById.get(p.formen_id);
        return formen?.bolge_muhendisi_id === parkFilterBM;
      });
    } else if (isMudur && parkFilterMY !== "all") {
      list = list.filter((p) => {
        const formen = personnelById.get(p.formen_id);
        const bm = personnelById.get(formen?.bolge_muhendisi_id);
        return bm?.mudur_yardimcisi_id === parkFilterMY;
      });
    } else if (isMudurYardimcisi) {
      list = list.filter((p) => {
        const formen = personnelById.get(p.formen_id);
        const bm = personnelById.get(formen?.bolge_muhendisi_id);
        return bm?.mudur_yardimcisi_id === personnelId;
      });
    } else if (isBolgeMuhendisi) {
      list = list.filter((p) => {
        const formen = personnelById.get(p.formen_id);
        return formen?.bolge_muhendisi_id === personnelId;
      });
    }

    list = list.filter((p) => matchesRegionFilterForPerson(p, filterRegion));
    return sortByName(list);
  }, [
    userRole,
    personnel,
    personnelId,
    parkFilterFormen,
    parkFilterBM,
    parkFilterMY,
    personnelById,
    filterRegion,
    matchesRegionFilterForPerson,
    sortByName,
  ]);

  const selectedRegionLabel = useMemo(() => {
    if (filterRegion === "all") return "";
    if (filterRegion === "assigned") return "Bölge atanmış";
    if (filterRegion === "none") return "Bölge atanmamış";
    return regions.find((r) => r.id === filterRegion)?.name || "";
  }, [filterRegion, regions]);

  useEffect(() => {
    if (parkFilterMY !== "all" && !myOptions.some((p) => p.id === parkFilterMY)) setParkFilterMY("all");
  }, [parkFilterMY, myOptions]);

  useEffect(() => {
    if (parkFilterBM !== "all" && !bmOptions.some((p) => p.id === parkFilterBM)) setParkFilterBM("all");
  }, [parkFilterBM, bmOptions]);

  useEffect(() => {
    if (parkFilterFormen !== "all" && !formenOptions.some((p) => p.id === parkFilterFormen)) setParkFilterFormen("all");
  }, [parkFilterFormen, formenOptions]);

  useEffect(() => {
    if (parkFilterPG !== "all" && !pgOptions.some((p) => p.id === parkFilterPG)) setParkFilterPG("all");
  }, [parkFilterPG, pgOptions]);

  useEffect(() => {
    const nextFilterPersonnel =
      parkFilterPG !== "all"
        ? parkFilterPG
        : parkFilterFormen !== "all"
          ? parkFilterFormen
          : parkFilterBM !== "all"
            ? parkFilterBM
            : isMudur && parkFilterMY !== "all"
              ? parkFilterMY
              : "all";
    setFilterPersonnel(nextFilterPersonnel);
  }, [parkFilterMY, parkFilterBM, parkFilterFormen, parkFilterPG, userRole, setFilterPersonnel]);

  const renderParkRow = useCallback(({ index, style, data }) => {
    const park = data[index];
    if (!park) return null;
    return (
      <div
        style={style}
        id={`park-card-${park.id}`}
        data-testid={`park-row-${park.id}`}
        className={`grid grid-cols-[200px_1fr_120px_180px_140px_100px] gap-4 px-4 py-2 items-center border-b ${
          highlightedParkId === park.id ? "bg-green-100" : "bg-white"
        }`}
      >
        <div className="font-medium truncate">{park.name}</div>
        <div>
          {park.assigned_personnel_names && park.assigned_personnel_names.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {park.assigned_personnel_names.map((name, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {name}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">Atanmamış</span>
          )}
        </div>
        <div className="text-sm">
          {park.area ? (
            <div className="flex flex-col gap-1">
              <span>{park.area} m²</span>
              {park.grass_area && (
                <span className="text-xs text-green-600">
                  <Sprout className="h-3 w-3 inline mr-1" />
                  {park.grass_area} m²
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
        <div>
          <div className="flex flex-wrap gap-1 text-xs">
            <Badge variant="outline" className="text-xs bg-yellow-50">
              <LightPoleIcon className="h-3 w-3 mr-1 text-yellow-600" />
              {park.light_pole_count || 0}
            </Badge>
            <Badge variant="outline" className="text-xs bg-orange-50">
              <BenchIcon className="h-3 w-3 mr-1 text-orange-600" />
              {park.bench_count || 0}
            </Badge>
            <Badge variant="outline" className="text-xs bg-gray-50">
              <Trash className="h-3 w-3 mr-1 text-gray-600" />
              {park.trash_bin_count || 0}
            </Badge>
            <Badge variant="outline" className="text-xs bg-blue-50">
              <Home className="h-3 w-3 mr-1 text-blue-600" />
              {park.gazebo_count || 0}
            </Badge>
          </div>
        </div>
        <div>
          <div className="flex flex-wrap gap-1">
            {park.has_running_track && (
              <Badge variant="outline" className="text-xs bg-blue-50">
                <Footprints className="h-3 w-3" />
              </Badge>
            )}
            {park.has_playground && (
              <Badge variant="outline" className="text-xs bg-purple-50">
                <Baby className="h-3 w-3" />
              </Badge>
            )}
            {park.has_sports_equipment && (
              <Badge variant="outline" className="text-xs bg-orange-50">
                <Dumbbell className="h-3 w-3" />
              </Badge>
            )}
            {park.has_wc && (
              <Badge variant="outline" className="text-xs bg-gray-50">
                <Home className="h-3 w-3" />
              </Badge>
            )}
            {park.has_fountain && (
              <Badge variant="outline" className="text-xs bg-blue-50">
                <Droplet className="h-3 w-3" />
              </Badge>
            )}
            {park.has_parking && (
              <Badge variant="outline" className="text-xs bg-indigo-50">
                <ParkingCircle className="h-3 w-3" />
              </Badge>
            )}
            {!park.has_running_track &&
              !park.has_playground &&
              !park.has_sports_equipment &&
              !park.has_wc &&
              !park.has_fountain &&
              !park.has_parking && <span className="text-xs text-gray-400">-</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleParkEdit(park)}
              className="hover:bg-blue-100"
              data-testid={`view-park-${park.id}`}
              title="Detayları Görüntüle"
            >
              <Eye className="h-4 w-4 text-blue-600" />
            </Button>
            {canDeletePark && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  setParkToDelete(park);
                }}
                className="hover:bg-red-100"
                data-testid={`delete-park-${park.id}`}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }, [highlightedParkId, handleParkEdit, setParkToDelete, userRole, canDeletePark]);
  
  return (
    <TabsContent value="parks" className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow space-y-3">
              {/* Toplam Park Sayısı */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TreePine className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Parklar</h3>
                </div>
                <Badge variant="default" className="text-base px-4 py-2">
                  <span className="font-bold">{displayedParks.length}</span>
                  <span className="mx-1">/</span>
                  <span className="opacity-75">{parks.length}</span>
                  <span className="ml-1">Park</span>
                </Badge>
              </div>

              {/* Hiyerarşik İstatistikler - Formen/Bölge Mühendisi seçildiğinde */}
              {filterPersonnel !== "all" && (() => {
                const selectedPerson = personnel.find(p => p.id === filterPersonnel);
                
                if (selectedPerson && (selectedPerson.role === 'formen' || selectedPerson.role === 'bolge_muhendisi' || selectedPerson.role === 'mudur_yardimcisi')) {
                  // Altındaki TÜM personeli bul (recursive olarak)
                  const subordinates = getAllSubordinates(filterPersonnel, selectedPerson.role, personnel);

                  // Her personelin park sayısını hesapla
                  const subordinateStats = subordinates.map(sub => ({
                    person: sub,
                    parkCount: displayedParks.filter(park => 
                      park.assigned_personnel_ids && park.assigned_personnel_ids.includes(sub.id)
                    ).length
                  })).filter(stat => stat.parkCount > 0);

                  // Seçilen kişinin kendi parkları
                  const ownParks = displayedParks.filter(park => 
                    park.assigned_personnel_ids && park.assigned_personnel_ids.includes(filterPersonnel)
                  ).length;

                  return (
                    <div className="border-t pt-3 space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        Hiyerarşik Park Dağılımı
                      </h4>
                      
                      {/* Seçilen kişinin kendi parkları */}
                      {ownParks > 0 && (
                        <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${getRoleBadge(selectedPerson.role).className}`}>
                              {getRoleBadge(selectedPerson.role).label}
                            </Badge>
                            <span className="text-sm font-medium">{selectedPerson.name}</span>
                          </div>
                          <Badge variant="secondary">{ownParks} park</Badge>
                        </div>
                      )}

                      {/* Altındaki personel ve park sayıları */}
                      {subordinateStats.length > 0 && (
                        <div className="space-y-1">
                          {subordinateStats.map(stat => (
                            <div key={stat.person.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`text-xs ${getRoleBadge(stat.person.role).className}`}>
                                  {getRoleBadge(stat.person.role).label}
                                </Badge>
                                <span className="text-sm text-gray-700">{stat.person.name}</span>
                              </div>
                              <Badge variant="outline" className="text-xs">{stat.parkCount} park</Badge>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Toplam */}
                      <div className="flex flex-col gap-1 bg-green-50 px-3 py-2 rounded-md border-t-2 border-green-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-green-900">Toplam Sorumluluk</span>
                          <Badge className="bg-green-600 text-white">{displayedParks.length} park</Badge>
                        </div>
                        {subordinateStats.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Users className="h-3.5 w-3.5 text-green-700" />
                            <span className="text-xs text-green-700">
                              {subordinateStats.length} {subordinateStats.length === 1 ? 'Personel' : 'Personel'}
                              {' '}({subordinateStats.filter(s => s.person.role === 'park_gorevlisi').length} Park Görevlisi
                              {subordinateStats.filter(s => s.person.role === 'formen').length > 0 && 
                                `, ${subordinateStats.filter(s => s.person.role === 'formen').length} Formen`}
                              {subordinateStats.filter(s => s.person.role === 'bolge_muhendisi').length > 0 && 
                                `, ${subordinateStats.filter(s => s.person.role === 'bolge_muhendisi').length} Bölge Mühendisi`})
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {!["park_gorevlisi", "gorevli", "çim_ekibi"].includes(userRole) && (
              <ActiveDelegationsCard
                activeSubstitutions={activeSubstitutions}
                title="🔄 Aktif Vekaletler"
                personnel={personnel}
                regions={regions}
                parks={parks}
              />
            )}

            {/* Filters, Sort, View and Add Button */}
            <div className="flex flex-col gap-4 bg-white p-4 rounded-lg shadow">
              {/*
                Arama + filtreler — Sprint 4 F3: dinamik granular yetki tabanlı.
                Her filtre alanı ayrı `park.filtre.*` permission'ı ile kontrol edilir.
                Admin panelden hangi rolün hangi filtreyi göreceği checkbox ile ayarlanır.

                Veri kapsamı (regions listesinin hierarchy ile sınırlanması) korunur
                — bu bir izin değil scope sorunu; ayrı bir sprintte ele alınır.
              */}
              {(canSearch || hasAnyFilter) && (
                <div className="flex flex-col gap-3">
                  {canSearch && (
                    <div className={`relative w-full ${!hasAnyFilter ? "max-w-md" : ""}`}>
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Park, mahalle veya adres ile ara..."
                        value={parkSearchInput}
                        onChange={(e) => setParkSearchInput(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  )}

                  {hasAnyFilter && (
                    <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                      {canFilterBolge && (
                        <SearchableSelect
                          value={filterRegion}
                          onChange={(value) => {
                            setFilterRegion(value);
                            setParkFilterMY("all");
                            setParkFilterBM("all");
                            setParkFilterFormen("all");
                            setParkFilterPG("all");
                          }}
                          allLabel="Tüm Bölgeler"
                          searchPlaceholder="Bölge ara..."
                          className="w-full sm:w-[200px]"
                          options={[
                            { id: "assigned", name: "Bölge Atanmış" },
                            { id: "none", name: "Bölge Atanmamış" },
                            ...regions
                              .filter((r) => {
                                // Veri kapsamı: MY/BM kendi altındaki personelin bölgelerini görür
                                if (isMudurYardimcisi) {
                                  const subs = personnel.filter((p) => p.mudur_yardimcisi_id === personnelId);
                                  return subs.some((s) => s.region_ids?.includes(r.id));
                                }
                                if (isBolgeMuhendisi) {
                                  const subs = personnel.filter((p) => p.bolge_muhendisi_id === personnelId);
                                  return subs.some((s) => s.region_ids?.includes(r.id));
                                }
                                return true;
                              })
                              .map((region) => ({ id: region.id, name: region.name })),
                          ]}
                        />
                      )}

                      {canFilterMY && (
                        <SearchableSelect
                          value={parkFilterMY}
                          onChange={(value) => {
                            setParkFilterMY(value);
                            setParkFilterBM("all");
                            setParkFilterFormen("all");
                            setParkFilterPG("all");
                          }}
                          allLabel="Tüm Müdür Yardımcıları"
                          searchPlaceholder="Müdür yardımcısı ara..."
                          className="w-full sm:w-[220px]"
                          options={myOptions.map((p) => ({ id: p.id, name: p.name }))}
                        />
                      )}

                      {canFilterBM && (
                        <SearchableSelect
                          value={parkFilterBM}
                          onChange={(value) => {
                            setParkFilterBM(value);
                            setParkFilterFormen("all");
                            setParkFilterPG("all");
                          }}
                          allLabel="Tüm Bölge Mühendisleri"
                          searchPlaceholder="Bölge mühendisi ara..."
                          className="w-full sm:w-[220px]"
                          options={bmOptions.map((p) => ({ id: p.id, name: p.name }))}
                        />
                      )}

                      {canFilterFormen && (
                        <SearchableSelect
                          value={parkFilterFormen}
                          onChange={(value) => {
                            setParkFilterFormen(value);
                            setParkFilterPG("all");
                          }}
                          allLabel="Tüm Formenler"
                          searchPlaceholder="Formen ara..."
                          className="w-full sm:w-[220px]"
                          options={formenOptions.map((p) => ({ id: p.id, name: p.name }))}
                        />
                      )}

                      {canFilterPG && (
                        <SearchableSelect
                          value={parkFilterPG}
                          onChange={setParkFilterPG}
                          allLabel="Tüm Park Görevlileri"
                          searchPlaceholder="Park görevlisi ara..."
                          className="w-full sm:w-[220px]"
                          options={pgOptions.map((p) => ({ id: p.id, name: p.name }))}
                        />
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10 px-3"
                        onClick={() => {
                          setSearchTerm("");
                          setParkSearchInput("");
                          setFilterRegion("all");
                          setParkFilterMY("all");
                          setParkFilterBM("all");
                          setParkFilterFormen("all");
                          setParkFilterPG("all");
                          setFilterPersonnel("all");
                        }}
                      >
                        Filtreleri Temizle
                      </Button>
                    </div>
                  )}

                  {canSearch && !hasAnyFilter && (
                    <p className="text-xs text-gray-500">
                      Filtre yetkiniz yok; görüntüleyebileceğiniz parklar listelenir. Arama ile hızlıca bulabilirsiniz.
                    </p>
                  )}
                </div>
              )}

              {/* Aktif filtre rozetleri — filtre yetkisi olan rollere göre */}
              {hasAnyFilter && (
                filterRegion !== "all" ||
                parkFilterMY !== "all" ||
                parkFilterBM !== "all" ||
                parkFilterFormen !== "all" ||
                parkFilterPG !== "all"
              ) && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-gray-500">Aktif filtre:</span>
                  {canFilterBolge && filterRegion !== "all" && selectedRegionLabel && (
                    <Badge variant="outline">Bölge: {selectedRegionLabel}</Badge>
                  )}
                  {canFilterMY && parkFilterMY !== "all" && <Badge variant="outline">MY: {personnelById.get(parkFilterMY)?.name}</Badge>}
                  {canFilterBM && parkFilterBM !== "all" && <Badge variant="outline">BM: {personnelById.get(parkFilterBM)?.name}</Badge>}
                  {canFilterFormen && parkFilterFormen !== "all" && <Badge variant="outline">Formen: {personnelById.get(parkFilterFormen)?.name}</Badge>}
                  {canFilterPG && parkFilterPG !== "all" && <Badge variant="outline">Park Görevlisi: {personnelById.get(parkFilterPG)?.name}</Badge>}
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                {isMudur && <div className="flex-1"></div>}
                {isParkGorevlisi && (
                  <div className="flex-1">
                    <p className="text-gray-600">Sorumlu olduğunuz parklar aşağıda listelenmektedir</p>
                  </div>
                )}
                <div className="flex gap-2 w-full sm:w-auto">
                  {/* Sıralama Butonu */}
                  <Button
                    variant={parksSortOrder === "alphabetical" ? "default" : "outline"}
                    onClick={() => setParksSortOrder(parksSortOrder === "default" ? "alphabetical" : "default")}
                    className="flex-1 sm:flex-none"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    {parksSortOrder === "alphabetical" ? "A-Z" : "Sırala"}
                  </Button>
                  
                  {/* Görünüm Değiştirme Butonları */}
                  <div className="flex gap-1 border rounded-md p-1">
                    <Button
                      variant={parksViewMode === "cards" ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setParksViewMode("cards")}
                      className="px-3"
                      title="Kart Görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={parksViewMode === "table" ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setParksViewMode("table")}
                      className="px-3"
                      title="Liste Görünümü"
                    >
                      <ListIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={parksViewMode === "map" ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setParksViewMode("map")}
                      className="px-3"
                      title="Harita Görünümü"
                    >
                      <MapIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Yeni Park Butonu — park.olustur yetkisi */}
                  {canCreatePark && (
                    <Button onClick={() => { resetParkForm(); setIsParkDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Yeni Park
                    </Button>
                  )}
                  
                  {/* Park Dialog - herkes için (düzenleme/görüntüleme) */}
                  <DraggableModal
                    isOpen={isParkDialogOpen}
                    onClose={() => {
                      // Dirty check: editable modda kaydedilmemiş değişiklik varsa onay sor
                      if (!isFormReadOnly && isParkFormDirty) {
                        const ok = window.confirm("Kaydedilmemiş değişiklikleriniz var.\n\nKaydetmeden kapatılsın mı?");
                        if (!ok) return;
                      }
                      // Scroll pozisyonunu kaydet - main content container için
                      const mainContent = document.querySelector('.flex-1.overflow-auto');
                      const scrollTop = mainContent ? mainContent.scrollTop : window.scrollY;
                      setParkFormSnapshot(null);
                      setIsParkDialogOpen(false);
                      resetParkForm();
                      setLocationEditMode(false); // Konum düzenleme modunu sıfırla
                      // Scroll pozisyonunu restore et
                      requestAnimationFrame(() => {
                        setTimeout(() => {
                          if (mainContent) {
                            mainContent.scrollTop = scrollTop;
                          } else {
                            window.scrollTo(0, scrollTop);
                          }
                        }, 50);
                      });
                    }}
                    closeOnOverlayClick={false}
                    title={
                      !isEditingPark
                        ? "Yeni Park Ekle"
                        : isFormReadOnly
                          ? `Park Detayı${parkForm?.name ? `: ${parkForm.name}` : ''}`
                          : `Park Düzenle${parkForm?.name ? `: ${parkForm.name}` : ''}`
                    }
                    subtitle={
                      !isEditingPark
                        ? "Yeni park bilgilerini doldurun."
                        : isFormReadOnly
                          ? "Park bilgilerini görüntülüyorsunuz."
                          : "Düzenleme modu aktif. Değişiklikleri Kaydet veya Düzenlemeyi Bitir butonuyla iptal edebilirsiniz."
                    }
                    maxWidth="max-w-2xl"
                    footer={
                      isEditingPark && canEditPark ? (
                        <div className="flex justify-end gap-2">
                          {isFormReadOnly ? (
                            <Button onClick={handleEnterEditMode} className="bg-blue-600 hover:bg-blue-700">
                              <Edit className="h-4 w-4 mr-2" /> Düzenle
                            </Button>
                          ) : (
                            <>
                              <Button variant="outline" onClick={handleExitEditMode}>
                                Düzenlemeyi Bitir
                              </Button>
                              <Button
                                type="button"
                                onClick={(e) => handleParkSubmit(e, drawnBoundaries)}
                                disabled={parkSubmitLoading}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                {parkSubmitLoading ? "Kaydediliyor..." : "Kaydet"}
                              </Button>
                            </>
                          )}
                        </div>
                      ) : !isEditingPark ? (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            onClick={(e) => handleParkSubmit(e, drawnBoundaries)}
                            disabled={parkSubmitLoading}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {parkSubmitLoading ? "Kaydediliyor..." : "Ekle"}
                          </Button>
                        </div>
                      ) : null
                    }
                  >
                  <div className="max-h-[70vh] overflow-y-auto">
                  <Tabs defaultValue="general" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-4">
                      <TabsTrigger value="general" className="flex items-center gap-2">
                        <Edit className="h-4 w-4" />
                        <span>Genel</span>
                      </TabsTrigger>
                      {isEditingPark && (
                        <TabsTrigger value="revision" className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          <span>Revize</span>
                        </TabsTrigger>
                      )}
                    </TabsList>

                    {/* GENEL SEKMESİ */}
                    <TabsContent value="general" className="space-y-4">
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      if (isFormReadOnly) {
                        toast.error("Değişiklik yapma yetkiniz bulunmamaktadır");
                        return;
                      }
                      handleParkSubmit(e, drawnBoundaries);
                    }} className="space-y-4">
                      {isFormReadOnly && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                          <p className="text-amber-700 text-sm font-medium">
                            Bu formu sadece görüntüleme modunda açtınız. Değişiklik yapamazsınız.
                          </p>
                        </div>
                      )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="park-name">Park Adı *</Label>
                        <DebouncedInput
                          id="park-name"
                          value={parkForm.name}
                          onChange={(value) => setParkForm(prev => ({...prev, name: value}))}
                          required
                          disabled={isFormReadOnly}
                          className={isFormReadOnly ? "bg-gray-100" : ""}
                        />
                      </div>

                      <div>
                        <Label htmlFor="park-neighborhood">Mahalle</Label>
                        <DebouncedInput
                          id="park-neighborhood"
                          value={parkForm.neighborhood}
                          onChange={(value) => setParkForm(prev => ({...prev, neighborhood: value}))}
                          placeholder="Örn: Atatürk Mahallesi"
                          disabled={isFormReadOnly}
                          className={isFormReadOnly ? "bg-gray-100" : ""}
                        />
                      </div>

                      <div>
                        <Label htmlFor="park-address">Açık Adres</Label>
                        <DebouncedInput
                          id="park-address"
                          value={parkForm.address}
                          onChange={(value) => setParkForm(prev => ({...prev, address: value}))}
                          placeholder="Örn: Cumhuriyet Cad. No:123"
                          disabled={isFormReadOnly}
                          className={isFormReadOnly ? "bg-gray-100" : ""}
                        />
                      </div>

                      {/* Koordinat Seçimi */}
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <h4 className="font-semibold text-md mb-3 flex items-center gap-2">
                          <TreePine className="h-4 w-4 text-green-600" />
                          Park Konumu (İsteğe Bağlı)
                        </h4>
                        
                        {/* Adres'ten Koordinat Bul Butonu */}
                        {parkForm.address && (
                          <div className="mb-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                if (!parkForm.address) {
                                  toast.error("Lütfen önce adres girin");
                                  return;
                                }
                                
                                setIsGeocodingAddress(true);
                                try {
                                  const response = await axios.get(`${API}/api/geocode`, {
                                    params: { address: parkForm.address }
                                  });
                                  
                                  if (response.data.success) {
                                    setParkForm(prev => ({
                                      ...prev,
                                      latitude: response.data.latitude,
                                      longitude: response.data.longitude,
                                      coordinates: `${response.data.latitude}, ${response.data.longitude}`
                                    }));
                                    toast.success(`Koordinat bulundu: ${response.data.display_name}`);
                                  }
                                } catch (error) {
                                  console.error("Geocoding error:", error);
                                  toast.error(error.response?.data?.detail || "Adres bulunamadı");
                                } finally {
                                  setIsGeocodingAddress(false);
                                }
                              }}
                              disabled={isGeocodingAddress || isFormReadOnly}
                            >
                              {isGeocodingAddress ? "Aranıyor..." : "📍 Adresten Koordinat Bul"}
                            </Button>
                          </div>
                        )}
                        
                        {/* Manuel Koordinat Girişi */}
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <Label htmlFor="park-latitude">Enlem (Latitude)</Label>
                            <Input
                              id="park-latitude"
                              type="number"
                              step="any"
                              value={parkForm.latitude || ""}
                              onChange={(e) => setParkForm({
                                ...parkForm,
                                latitude: e.target.value ? parseFloat(e.target.value) : null,
                                coordinates: e.target.value && parkForm.longitude
                                  ? `${e.target.value}, ${parkForm.longitude}`
                                  : ""
                              })}
                              placeholder="Örn: 39.9334"
                              disabled={isFormReadOnly}
                              className={isFormReadOnly ? "bg-gray-100" : ""}
                            />
                          </div>
                          <div>
                            <Label htmlFor="park-longitude">Boylam (Longitude)</Label>
                            <Input
                              id="park-longitude"
                              type="number"
                              step="any"
                              value={parkForm.longitude || ""}
                              onChange={(e) => setParkForm({
                                ...parkForm,
                                longitude: e.target.value ? parseFloat(e.target.value) : null,
                                coordinates: parkForm.latitude && e.target.value
                                  ? `${parkForm.latitude}, ${e.target.value}`
                                  : ""
                              })}
                              placeholder="Örn: 32.8597"
                              disabled={isFormReadOnly}
                              className={isFormReadOnly ? "bg-gray-100" : ""}
                            />
                          </div>
                        </div>
                        
                        {/* Mini Harita Preview - Koordinatlar seçildiğinde göster */}
                        {parkForm.latitude && parkForm.longitude && !showMap && (
                          <div className="mb-3">
                            <Label className="text-xs text-muted-foreground mb-1 block">Seçilen Konum</Label>
                            <div className="border rounded-lg overflow-hidden" style={{ height: '150px' }}>
                              <MapContainer
                                key={`mini-${parkForm.latitude}-${parkForm.longitude}`}
                                center={[parkForm.latitude, parkForm.longitude]}
                                zoom={16}
                                style={{ height: '100%', width: '100%' }}
                                zoomControl={false}
                                dragging={false}
                                scrollWheelZoom={false}
                              >
                                <TileLayer
                                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <Marker position={[parkForm.latitude, parkForm.longitude]} icon={pinIcon(NEARBY_SOURCES.park.color)} />
                                {drawnBoundaries.length > 0 && (
                                  <Polygon
                                    positions={drawnBoundaries}
                                    pathOptions={{
                                      color: '#4CAF50',
                                      fillColor: '#4CAF50',
                                      fillOpacity: 0.3,
                                      weight: 2
                                    }}
                                  />
                                )}
                              </MapContainer>
                            </div>
                          </div>
                        )}
                        
                        {/* Harita Butonları */}
                        <div className="flex gap-2 mb-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowMap(!showMap)}
                          >
                            {showMap ? "🗺️ Haritayı Gizle" : "🗺️ Haritadan Seç"}
                          </Button>
                          {/* Konum Düzenleme Modu butonu - sadece düzenleme yetkisi olanlar için */}
                          {/* Bu buton her zaman görünür, tıklanınca diğer butonlar açılır */}
                          {showMap && !isFormReadOnly && (
                            <Button
                              type="button"
                              variant={locationEditMode ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                const newMode = !locationEditMode;
                                setLocationEditMode(newMode);
                                if (newMode) {
                                  toast.info("Konum düzenleme modu aktif. Haritaya tıklayarak konum seçebilir veya çizim yapabilirsiniz.");
                                } else {
                                  setDrawingMode(false); // Düzenleme kapatılınca çizim modunu da kapat
                                }
                              }}
                              className={locationEditMode ? "bg-blue-600 hover:bg-blue-700" : ""}
                            >
                              {locationEditMode ? "🔓 Düzenleme Aktif" : "🔒 Konum Düzenle"}
                            </Button>
                          )}
                          {/* Çizim butonu - sadece konum düzenleme modu aktifken görünür */}
                          {showMap && !isFormReadOnly && locationEditMode && (
                            <Button
                              type="button"
                              variant={drawingMode ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                const starting = !drawingMode;
                                setDrawingMode(starting);
                                if (starting) {
                                  // Yeniden çizim: eski sınırı tamamen temizle ki sıfırdan başlansın
                                  // (eski poligon haritada kalıp geri gelmesin).
                                  setDrawnBoundaries([]);
                                  setParkForm(prev => ({ ...prev, boundaries: [] }));
                                  toast.info("Haritaya tıklayarak köşeleri ekleyin, sonra \"Çizimi Tamamla\"ya basın.");
                                }
                              }}
                              className={drawingMode ? "bg-orange-600 hover:bg-orange-700" : ""}
                            >
                              {drawingMode ? "✏️ Çizim Aktif" : "🔷 Park Sınırını Çiz"}
                            </Button>
                          )}
                          {/* Çizimi Tamamla - ilk noktaya isabet ettirme zorunluluğunu kaldırır */}
                          {showMap && !isFormReadOnly && locationEditMode && drawingMode && (
                            <Button
                              type="button"
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              disabled={drawnBoundaries.length < 3}
                              onClick={() => {
                                if (drawnBoundaries.length < 3) {
                                  toast.error("En az 3 köşe gerekli.");
                                  return;
                                }
                                setParkForm(prev => ({ ...prev, boundaries: drawnBoundaries }));
                                setDrawingMode(false);
                                toast.success(`✅ Park sınırı çizildi (${drawnBoundaries.length} nokta). Kaydetmeyi unutmayın.`);
                              }}
                            >
                              ✅ Çizimi Tamamla{drawnBoundaries.length >= 3 ? ` (${drawnBoundaries.length})` : ""}
                            </Button>
                          )}
                          {/* Sınırı temizle butonu - sadece konum düzenleme modu aktif ve sınır varsa görünür */}
                          {showMap && !isFormReadOnly && locationEditMode && drawnBoundaries.length > 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setDrawnBoundaries([]);
                                setParkForm({...parkForm, boundaries: []});
                                toast.success("Park sınırı temizlendi");
                              }}
                            >
                              🗑️ Sınırı Temizle
                            </Button>
                          )}
                        </div>
                        
                        {/* Harita */}
                        {showMap && (
                          <div className="border rounded-lg overflow-hidden relative" style={{ height: '400px' }}>
                            {isFormReadOnly && (
                              <div className="bg-amber-50 border-b border-amber-200 px-3 py-2 text-sm text-amber-700">
                                📍 Sadece görüntüleme modu - Konum ve çizim değiştirilemez
                              </div>
                            )}
                            {!isFormReadOnly && locationEditMode && !drawingMode && (
                              <div className="bg-blue-50 border-b border-blue-200 px-3 py-2 text-sm text-blue-700">
                                📍 Düzenleme aktif - Haritaya tıklayarak yeni konum seçin
                              </div>
                            )}
                            {!isFormReadOnly && locationEditMode && drawingMode && (
                              <div className="bg-orange-50 border-b border-orange-200 px-3 py-2 text-sm text-orange-700">
                                ✏️ Çizim aktif - Haritaya tıklayarak park sınırını çizin
                              </div>
                            )}
                            {!isFormReadOnly && !locationEditMode && (
                              <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 text-sm text-gray-600">
                                🔒 Konum kilitli - Değiştirmek için &quot;Konum Düzenle&quot; butonuna tıklayın
                              </div>
                            )}
                            <MapLayerToggle value={formMapLayer} onChange={setFormMapLayer} floating className="!top-12" />
                            {!isFormReadOnly && (
                              <MapSearchBox apiBase={API} onResult={(lat, lng) => setFormSearchCenter([lat, lng])} className="top-12 left-2" />
                            )}
                            <MapContainer
                              center={[parkForm.latitude || 39.9334, parkForm.longitude || 32.8597]}
                              zoom={13}
                              style={{ height: 'calc(100% - 36px)', width: '100%' }}
                            >
                              <MapLayerTile layer={formMapLayer} />
                              <RecenterMap center={formSearchCenter} />

                              {parkForm.latitude && parkForm.longitude && (
                                <Marker position={[parkForm.latitude, parkForm.longitude]} icon={pinIcon(NEARBY_SOURCES.park.color)}>
                                  <Popup>
                                    {parkForm.name || "Park Konumu"}<br/>
                                    {parkForm.latitude}, {parkForm.longitude}
                                  </Popup>
                                </Marker>
                              )}
                              
                              {/* Konum düzenleme modu aktifken tıklama ile konum seçimi */}
                              {!drawingMode && !isFormReadOnly && locationEditMode && (
                                <LocationMarker 
                                  setParkForm={setParkForm} 
                                  parkForm={parkForm}
                                />
                              )}
                              
                              {/* Çizim modu - polygon çizimi - sadece düzenleme yetkisi olanlar için */}
                              {drawingMode && !isFormReadOnly && <DrawingMarker 
                                drawnBoundaries={drawnBoundaries}
                                setDrawnBoundaries={setDrawnBoundaries}
                                setParkForm={setParkForm}
                                parkForm={parkForm}
                                setDrawingMode={setDrawingMode}
                              />}
                              
                              {/* Çizilmiş sınırı göster */}
                              {drawnBoundaries.length > 0 && (
                                <Polygon
                                  positions={drawnBoundaries}
                                  pathOptions={{
                                    color: '#4CAF50',
                                    fillColor: '#4CAF50',
                                    fillOpacity: 0.3,
                                    weight: 3
                                  }}
                                />
                              )}
                              
                              {/* Mevcut boundaries varsa göster */}
                              {!drawingMode && parkForm.boundaries && parkForm.boundaries.length > 0 && (
                                <Polygon
                                  positions={parkForm.boundaries}
                                  pathOptions={{
                                    color: '#2196F3',
                                    fillColor: '#2196F3',
                                    fillOpacity: 0.2,
                                    weight: 2
                                  }}
                                />
                              )}
                            </MapContainer>
                            <p className="text-xs text-gray-500 mt-2 px-2 pb-2">
                              {isFormReadOnly 
                                ? "📍 Haritayı yakınlaştırıp uzaklaştırarak park konumunu görüntüleyebilirsiniz"
                                : locationEditMode
                                  ? `🎯 Konum düzenleme aktif - Haritaya tıklayarak yeni konum seçin ${drawnBoundaries.length > 0 ? `• ${drawnBoundaries.length} nokta işaretlendi` : ''}`
                                  : drawingMode
                                    ? `✏️ Çizim modu aktif - Haritaya tıklayarak park sınırını çizin`
                                    : `🔒 Konum kilitli - "Konum Düzenle" butonuna tıklayarak konum değiştirebilirsiniz`
                              }
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <div className="flex items-center justify-between mb-2">
                          <Label htmlFor="park-region">Bölge</Label>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setQuickAddContext("park");
                              setIsQuickRegionDialogOpen(true);
                            }}
                            className="text-xs"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Yeni Bölge Ekle
                          </Button>
                        </div>
                        <SearchableSelect
                          value={parkForm.region_id || "none"}
                          onChange={(value) => setParkForm({ ...parkForm, region_id: value === "none" ? null : value })}
                          allValue="none"
                          allLabel="Bölge atama"
                          searchPlaceholder="Bölge ara..."
                          options={[...regions]
                            .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "tr"))
                            .map(region => ({ id: region.id, name: region.name }))}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Parkın hangi bölgeye ait olduğunu seçin
                        </p>
                      </div>

                      {/* Personel Atama Bölümü */}
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <h4 className="font-semibold text-md mb-3 flex items-center gap-2">
                          <Users className="h-4 w-4 text-purple-600" />
                          Personel Ataması
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* ADIM 1: Önce Formen Seç */}
                          <div className="md:col-span-2">
                            <Label htmlFor="temp-formen" className="flex items-center gap-2">
                              <HardHat className="h-4 w-4 text-yellow-600" />
                              1. Adım: Formen Seçin
                            </Label>
                            <SearchableSelect
                              value={parkForm.assigned_formen_id || "none"}
                              onChange={(value) => {
                                const selectedFormenId = value === "none" ? null : value;
                                setParkForm(prev => ({
                                  ...prev,
                                  assigned_formen_id: selectedFormenId,
                                  assigned_park_gorevlisi_id: null // Formen değişince park görevlisini sıfırla
                                }));

                                // Formen seçilirse, onun hiyerarşisini doldur
                                if (selectedFormenId) {
                                  const selectedFormen = personnel.find(p => p.id === selectedFormenId);
                                  if (selectedFormen) {
                                    setParkForm(prev => ({
                                      ...prev,
                                      assigned_bolge_muhendisi_id: selectedFormen.bolge_muhendisi_id || null,
                                      assigned_mudur_yardimcisi_id: selectedFormen.mudur_yardimcisi_id || null
                                    }));
                                  }
                                } else {
                                  setParkForm(prev => ({
                                    ...prev,
                                    assigned_bolge_muhendisi_id: null,
                                    assigned_mudur_yardimcisi_id: null
                                  }));
                                }
                              }}
                              disabled={isFormReadOnly}
                              allValue="none"
                              allLabel="Formen seçmeyin"
                              searchPlaceholder="Formen ara..."
                              className={isFormReadOnly ? "bg-gray-100" : ""}
                              options={personnel
                                .filter(p => p.role === 'formen')
                                .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                                .map(person => ({ id: person.id, name: person.name }))}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Formenler arasından seçim yapın (10-15 kişi)
                            </p>
                          </div>

                          {/* ADIM 2: Formen Seçildiyse Park Görevlisi Göster (OPSİYONEL) */}
                          <div className="md:col-span-2">
                            <Label htmlFor="park-gorevlisi" className={!parkForm.assigned_formen_id ? "text-gray-400" : ""}>
                              2. Adım: Park Görevlisi Seçin (Opsiyonel)
                            </Label>
                            <SearchableSelect
                              value={parkForm.assigned_park_gorevlisi_id || "none"}
                              onChange={(value) => {
                                const selectedId = value === "none" ? null : value;
                                setParkForm({ ...parkForm, assigned_park_gorevlisi_id: selectedId });
                              }}
                              disabled={isFormReadOnly || !parkForm.assigned_formen_id}
                              allValue="none"
                              allLabel="Atanmış park görevlisi yok"
                              searchPlaceholder="Park görevlisi ara..."
                              className={(isFormReadOnly || !parkForm.assigned_formen_id) ? "bg-gray-100" : ""}
                              options={(parkForm.assigned_formen_id ? personnel
                                .filter(p => p.role === 'park_gorevlisi' && p.formen_id === parkForm.assigned_formen_id)
                                .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                                : []).map(person => ({ id: person.id, name: person.name }))}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {parkForm.assigned_formen_id 
                                ? `💡 Opsiyonel - Formen tek başına da atanabilir. (${personnel.filter(p => p.role === 'park_gorevlisi' && p.formen_id === parkForm.assigned_formen_id).length} park görevlisi mevcut)`
                                : "Park görevlisi seçmek için önce formen seçin"}
                            </p>
                          </div>

                          {/* Bağlı Olduğu Formen (Otomatik) */}
                          <div>
                            <Label className="flex items-center gap-2">
                              <span className="text-gray-500">Bağlı Olduğu Formen</span>
                            </Label>
                            <Input
                              value={parkForm.assigned_formen_id ? personnel.find(p => p.id === parkForm.assigned_formen_id)?.name || 'Bulunamadı' : 'Atanmadı'}
                              disabled
                              className="bg-gray-50 text-gray-600"
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              Park görevlisinin bağlı olduğu formen
                            </p>
                          </div>

                          {/* Bağlı Olduğu Bölge Mühendisi (Otomatik) */}
                          <div>
                            <Label className="flex items-center gap-2">
                              <span className="text-gray-500">Bağlı Olduğu Bölge Mühendisi</span>
                            </Label>
                            <Input
                              value={parkForm.assigned_bolge_muhendisi_id ? personnel.find(p => p.id === parkForm.assigned_bolge_muhendisi_id)?.name || 'Bulunamadı' : 'Atanmadı'}
                              disabled
                              className="bg-gray-50 text-gray-600"
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              Park görevlisinin bağlı olduğu bölge mühendisi
                            </p>
                          </div>

                          {/* Bağlı Olduğu Müdür Yardımcısı (Otomatik) */}
                          <div className="md:col-span-2">
                            <Label className="flex items-center gap-2">
                              <span className="text-gray-500">Bağlı Olduğu Müdür Yardımcısı</span>
                            </Label>
                            <Input
                              value={parkForm.assigned_mudur_yardimcisi_id ? personnel.find(p => p.id === parkForm.assigned_mudur_yardimcisi_id)?.name || 'Bulunamadı' : 'Atanmadı'}
                              disabled
                              className="bg-gray-50 text-gray-600"
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              Park görevlisinin bağlı olduğu müdür yardımcısı
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="park-area">Toplam Alan (m²)</Label>
                        <DebouncedInput
                          id="park-area"
                          type="number"
                          step="0.01"
                          value={parkForm.area}
                          onChange={(value) => setParkForm(prev => ({ ...prev, area: value }))}
                          disabled={isFormReadOnly}
                          className={isFormReadOnly ? "bg-gray-100" : ""}
                        />
                      </div>

                      <div>
                        <Label htmlFor="grass-area" className="flex items-center gap-2">
                          <Sprout className="h-4 w-4 text-green-600" />
                          Çim Alanı (m²)
                        </Label>
                        <DebouncedInput
                          id="grass-area"
                          type="number"
                          step="0.01"
                          value={parkForm.grass_area}
                          onChange={(value) => setParkForm(prev => ({ ...prev, grass_area: value }))}
                          disabled={isFormReadOnly}
                          className={isFormReadOnly ? "bg-gray-100" : ""}
                        />
                      </div>

                      <div>
                        <Label htmlFor="hard-surface-area">Sert Zemin (m²)</Label>
                        <DebouncedInput
                          id="hard-surface-area"
                          type="number"
                          step="0.01"
                          value={parkForm.hard_surface_area}
                          onChange={(value) => setParkForm(prev => ({ ...prev, hard_surface_area: value }))}
                          disabled={isFormReadOnly}
                          className={isFormReadOnly ? "bg-gray-100" : ""}
                        />
                      </div>

                      <div>
                        <Label htmlFor="playground-area" className="flex items-center gap-2">
                          <Baby className="h-4 w-4 text-purple-600" />
                          Çocuk Oyun Alanı (m²)
                        </Label>
                        <DebouncedInput
                          id="playground-area"
                          type="number"
                          step="0.01"
                          value={parkForm.playground_area}
                          onChange={(value) => setParkForm(prev => ({ ...prev, playground_area: value }))}
                          disabled={isFormReadOnly}
                          className={isFormReadOnly ? "bg-gray-100" : ""}
                        />
                      </div>
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                        Envanter Bilgileri
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="light-pole-count" className="flex items-center gap-2">
                            <Lightbulb className="h-4 w-4 text-yellow-600" />
                            Elektrik Direği
                          </Label>
                          <Input
                            id="light-pole-count"
                            type="number"
                            min="0"
                            value={parkForm.light_pole_count}
                            onChange={(e) => setParkForm({...parkForm, light_pole_count: e.target.value})}
                            disabled={isFormReadOnly}
                            className={isFormReadOnly ? "bg-gray-100" : ""}
                          />
                        </div>

                        <div>
                          <Label htmlFor="bench-count" className="flex items-center gap-2">
                            <BenchIcon className="h-4 w-4 text-orange-600" />
                            Bank Sayısı
                          </Label>
                          <Input
                            id="bench-count"
                            type="number"
                            min="0"
                            value={parkForm.bench_count}
                            onChange={(e) => setParkForm({...parkForm, bench_count: e.target.value})}
                            disabled={isFormReadOnly}
                            className={isFormReadOnly ? "bg-gray-100" : ""}
                          />
                        </div>

                        <div>
                          <Label htmlFor="trash-bin-count" className="flex items-center gap-2">
                            <Trash className="h-4 w-4 text-gray-600" />
                            Çöp Kovası
                          </Label>
                          <Input
                            id="trash-bin-count"
                            type="number"
                            min="0"
                            value={parkForm.trash_bin_count}
                            onChange={(e) => setParkForm({...parkForm, trash_bin_count: e.target.value})}
                            disabled={isFormReadOnly}
                            className={isFormReadOnly ? "bg-gray-100" : ""}
                          />
                        </div>

                        <div>
                          <Label htmlFor="gazebo-count" className="flex items-center gap-2">
                            <Home className="h-4 w-4 text-blue-600" />
                            Kamelya Sayısı
                          </Label>
                          <Input
                            id="gazebo-count"
                            type="number"
                            min="0"
                            value={parkForm.gazebo_count}
                            onChange={(e) => setParkForm({...parkForm, gazebo_count: e.target.value})}
                            disabled={isFormReadOnly}
                            className={isFormReadOnly ? "bg-gray-100" : ""}
                          />
                        </div>

                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h3 className="font-semibold text-lg mb-4">Tesisler</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="running-track"
                            checked={parkForm.has_running_track}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_running_track: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="running-track" className="flex items-center gap-2 cursor-pointer">
                            <Footprints className="h-4 w-4 text-blue-600" />
                            Yürüme/Koşu Yolu
                          </Label>
                        </div>

                        {/* Yürüme/Koşu Yolu Detayları */}
                        {parkForm.has_running_track && (
                          <>
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                              <div>
                                <Label htmlFor="running-track-length" className="text-xs font-semibold text-blue-900">
                                  Uzunluk (metre)
                                </Label>
                                <Input
                                  id="running-track-length"
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  value={parkForm.running_track_length}
                                  onChange={(e) => setParkForm({...parkForm, running_track_length: e.target.value})}
                                  placeholder="Örn: 500"
                                  disabled={isFormReadOnly}
                                  className={`mt-1 ${isFormReadOnly ? "bg-gray-100" : ""}`}
                                />
                              </div>

                              <div>
                                <Label htmlFor="running-track-width" className="text-xs font-semibold text-blue-900">
                                  Genişlik (metre)
                                </Label>
                                <Input
                                  id="running-track-width"
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  value={parkForm.running_track_width}
                                  onChange={(e) => setParkForm({...parkForm, running_track_width: e.target.value})}
                                  placeholder="Örn: 2"
                                  disabled={isFormReadOnly}
                                  className={`mt-1 ${isFormReadOnly ? "bg-gray-100" : ""}`}
                                />
                              </div>

                              <div>
                                <Label htmlFor="running-track-type" className="text-xs font-semibold text-blue-900">
                                  Yol Tipi
                                </Label>
                                <SearchableSelect
                                  value={parkForm.running_track_type || "yok"}
                                  onChange={(value) => setParkForm({...parkForm, running_track_type: value})}
                                  disabled={isFormReadOnly}
                                  showAllOption={false}
                                  placeholder="Seçin"
                                  searchPlaceholder="Zemin ara..."
                                  className={`mt-1 ${isFormReadOnly ? "bg-gray-100" : ""}`}
                                  options={[
                                    { id: "yok", name: "Belirtilmemiş" },
                                    { id: "kum", name: "Kum" },
                                    { id: "sentetik_cim", name: "Suni Çim" },
                                    { id: "kaucuk", name: "Kauçuk" },
                                    { id: "sentetik_cim_ve_kaucuk", name: "Suni Çim + Kauçuk" },
                                    { id: "asfalt", name: "Asfalt" },
                                    { id: "parke", name: "Parke Taş" },
                                    { id: "kilit_tasi", name: "Kilit Taşı" },
                                  ]}
                                />
                              </div>

                              <div className="md:col-span-3 bg-white rounded p-3 border border-blue-300">
                                <p className="text-sm font-semibold text-blue-900 mb-1">Toplam Alan</p>
                                <p className="text-2xl font-bold text-blue-600">
                                  {(parseFloat(parkForm.running_track_length || 0) * parseFloat(parkForm.running_track_width || 0)).toFixed(2)} m²
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                  {parkForm.running_track_length || 0} m × {parkForm.running_track_width || 0} m
                                </p>
                              </div>
                            </div>
                          </>
                        )}

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="playground"
                            checked={parkForm.has_playground}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_playground: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="playground" className="flex items-center gap-2 cursor-pointer">
                            <Baby className="h-4 w-4 text-purple-600" />
                            Çocuk Oyun Grubu
                          </Label>
                        </div>

                        {/* Çocuk Oyun Grubu Zemin Detayları */}
                        {parkForm.has_playground && (
                          <div className="md:col-span-2 p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <Label htmlFor="playground-surface-type" className="text-sm font-semibold text-purple-900 mb-2 block">
                              Zemin Tipi
                            </Label>
                            <SearchableSelect
                              value={parkForm.playground_surface_type || "yok"}
                              onChange={(value) => setParkForm({...parkForm, playground_surface_type: value})}
                              disabled={isFormReadOnly}
                              showAllOption={false}
                              placeholder="Zemin tipi seçin"
                              searchPlaceholder="Zemin ara..."
                              className={isFormReadOnly ? "bg-gray-100" : ""}
                              options={[
                                { id: "yok", name: "Belirtilmemiş" },
                                { id: "dere_cakili", name: "Dere Çakılı" },
                                { id: "kaucuk", name: "Kauçuk" },
                                { id: "kum", name: "Kum" },
                                { id: "sentetik_cim", name: "Suni Çim" },
                                { id: "ahsap", name: "Ahşap" },
                              ]}
                            />
                            <p className="text-xs text-purple-600 mt-2">
                              Oyun alanı: {parkForm.playground_area || 0} m²
                              <span className="text-gray-500 ml-2">(Yukarıda &quot;Çocuk Oyun Alanı&quot; kısmından düzenleyin)</span>
                            </p>
                          </div>
                        )}

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="sports-equipment"
                            checked={parkForm.has_sports_equipment}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_sports_equipment: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="sports-equipment" className="flex items-center gap-2 cursor-pointer">
                            <Dumbbell className="h-4 w-4 text-orange-600" />
                            Spor Aleti
                          </Label>
                        </div>

                        {/* Spor Aleti Zemin Detayları */}
                        {parkForm.has_sports_equipment && (
                          <div className="md:col-span-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="sports-equipment-area" className="text-sm font-semibold text-orange-900 mb-2 block">
                                  Spor Aleti Alanı (m²)
                                </Label>
                                <Input
                                  id="sports-equipment-area"
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  value={parkForm.sports_equipment_area}
                                  onChange={(e) => setParkForm({...parkForm, sports_equipment_area: e.target.value})}
                                  placeholder="Örn: 150"
                                  disabled={isFormReadOnly}
                                  className={isFormReadOnly ? "bg-gray-100" : ""}
                                />
                              </div>

                              <div>
                                <Label htmlFor="sports-equipment-surface-type" className="text-sm font-semibold text-orange-900 mb-2 block">
                                  Zemin Tipi
                                </Label>
                                <SearchableSelect
                                  value={parkForm.sports_equipment_surface_type || "yok"}
                                  onChange={(value) => setParkForm({...parkForm, sports_equipment_surface_type: value})}
                                  disabled={isFormReadOnly}
                                  showAllOption={false}
                                  placeholder="Zemin tipi seçin"
                                  searchPlaceholder="Zemin ara..."
                                  className={isFormReadOnly ? "bg-gray-100" : ""}
                                  options={[
                                    { id: "yok", name: "Belirtilmemiş" },
                                    { id: "kaucuk", name: "Kauçuk" },
                                    { id: "asfalt", name: "Asfalt" },
                                    { id: "sentetik_cim", name: "Suni Çim" },
                                    { id: "kilit_tasi", name: "Kilit Taşı" },
                                    { id: "diger", name: "Diğer" },
                                  ]}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="wc"
                            checked={parkForm.has_wc}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_wc: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="wc" className="flex items-center gap-2 cursor-pointer">
                            <Home className="h-4 w-4 text-gray-600" />
                            WC
                          </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="fountain"
                            checked={parkForm.has_fountain}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_fountain: checked, fountain_status: checked ? "calisiyor" : "yok"})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="fountain" className="flex items-center gap-2 cursor-pointer">
                            <Droplet className="h-4 w-4 text-blue-400" />
                            Havuz
                          </Label>
                        </div>

                        {/* Havuz Durum Seçimi */}
                        {parkForm.has_fountain && (
                          <div className="md:col-span-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <Label className="text-sm font-semibold text-blue-900 mb-2 block">Havuz Durumu</Label>
                            <div className="flex gap-4">
                              <label className={`flex items-center gap-2 ${isFormReadOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
                                <input
                                  type="radio"
                                  name="fountain_status"
                                  value="calisiyor"
                                  checked={parkForm.fountain_status === "calisiyor"}
                                  onChange={(e) => setParkForm({...parkForm, fountain_status: e.target.value})}
                                  disabled={isFormReadOnly}
                                  className="w-4 h-4 text-green-600"
                                />
                                <span className="text-sm flex items-center gap-1">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  Çalışıyor
                                </span>
                              </label>
                              <label className={`flex items-center gap-2 ${isFormReadOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
                                <input
                                  type="radio"
                                  name="fountain_status"
                                  value="arizali"
                                  checked={parkForm.fountain_status === "arizali"}
                                  onChange={(e) => setParkForm({...parkForm, fountain_status: e.target.value})}
                                  disabled={isFormReadOnly}
                                  className="w-4 h-4 text-red-600"
                                />
                                <span className="text-sm flex items-center gap-1">
                                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                  Arızalı
                                </span>
                              </label>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="parking"
                            checked={parkForm.has_parking}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_parking: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="parking" className="flex items-center gap-2 cursor-pointer">
                            <ParkingCircle className="h-4 w-4 text-indigo-600" />
                            Otopark
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <Dumbbell className="h-5 w-5 text-orange-600" />
                        Spor Sahaları
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="basketball-court"
                            checked={parkForm.has_basketball_court}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_basketball_court: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="basketball-court" className="flex items-center gap-2 cursor-pointer">
                            🏀 Basketbol Sahası
                          </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="football-field"
                            checked={parkForm.has_football_field}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_football_field: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="football-field" className="flex items-center gap-2 cursor-pointer">
                            ⚽ Futbol Sahası
                          </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="volleyball-court"
                            checked={parkForm.has_volleyball_court}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_volleyball_court: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="volleyball-court" className="flex items-center gap-2 cursor-pointer">
                            🏐 Voleybol Sahası
                          </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="tennis-court"
                            checked={parkForm.has_tennis_court}
                            onCheckedChange={(checked) => setParkForm({...parkForm, has_tennis_court: checked})}
                            disabled={isFormReadOnly}
                          />
                          <Label htmlFor="tennis-court" className="flex items-center gap-2 cursor-pointer">
                            🎾 Tenis Kortu
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <Label htmlFor="irrigation-system" className="flex items-center gap-2 mb-2">
                        <Droplets className="h-4 w-4 text-blue-500" />
                        Sulama Sistemi
                      </Label>
                      <SearchableSelect
                        value={parkForm.irrigation_system}
                        onChange={(value) => setParkForm({...parkForm, irrigation_system: value})}
                        disabled={isFormReadOnly}
                        showAllOption={false}
                        placeholder="Sulama sistemi seçin"
                        searchPlaceholder="Sistem ara..."
                        className={isFormReadOnly ? "bg-gray-100" : ""}
                        options={[
                          { id: "yok", name: "Yok" },
                          { id: "otomatik", name: "Otomatik" },
                          { id: "manuel", name: "Manuel" },
                          { id: "hortumla", name: "Hortumla" },
                        ]}
                      />
                    </div>

                    <div>
                      <Label htmlFor="park-description">Açıklama/Notlar</Label>
                      <Input
                        id="park-description"
                        value={parkForm.description}
                        onChange={(e) => setParkForm({...parkForm, description: e.target.value})}
                        placeholder="Ek bilgiler..."
                        disabled={isFormReadOnly}
                        className={isFormReadOnly ? "bg-gray-100" : ""}
                      />
                    </div>

                      {/* Submit butonu DraggableModal footer'ına taşındı (unified akış).
                          Form yine onSubmit ile çalışır (Enter tuşu submit edebilir). */}
                    </form>
                    </TabsContent>

                    {/* REVİZE SEKMESİ */}
                    {isEditingPark && (
                      <TabsContent value="revision" className="space-y-4">
                        <ParkRevisionTab
                          parkId={parkForm.id}
                          parkName={parkForm.name}
                          isFormReadOnly={isFormReadOnly}
                          API={API}
                          revisions={revisions}
                          parks={parks}
                          onDeleteRevision={onDeleteRevision}
                          onDeleteCompletedRevisionHistory={onDeleteCompletedRevisionHistory}
                          userRole={userRole}
                          personnelId={personnelId}
                        />
                      </TabsContent>
                    )}
                  </Tabs>
                  </div>
              </DraggableModal>
                </div>
              </div>
            </div>

            {/* Parks List - Card or Table View */}
            {loadingParks ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="h-4 bg-gray-100 rounded"></div>
                        <div className="h-4 bg-gray-100 rounded w-5/6"></div>
                        <div className="h-4 bg-gray-100 rounded w-4/6"></div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : displayedParks.length === 0 ? (
              <div className="text-center py-16 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border-2 border-dashed border-green-200">
                <TreePine className="h-20 w-20 text-green-400 mx-auto mb-4 animate-pulse" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {isParkGorevlisi ? "Park Bulunamadı" : "Henüz Park Yok"}
                </h3>
                <p className="text-gray-500 mb-4">
                  {isParkGorevlisi ? "Size atanmış park bulunmamaktadır" : "Sisteme henüz park eklenmemiş"}
                </p>
                {userRole !== "gorevli" && (
                  <Button onClick={() => setIsParkDialogOpen(true)} className="mt-2">
                    <Plus className="h-4 w-4 mr-2" />
                    İlk Parkı Ekle
                  </Button>
                )}
              </div>
            ) : parksViewMode === "cards" ? (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {parkCards.map(park => (
                  <Card
                    key={park.id}
                    id={`park-card-${park.id}`}
                    className={`hover:shadow-lg transition-all ${highlightedParkId === park.id ? 'ring-2 ring-green-500 ring-offset-2 bg-green-50' : ''}`}
                    data-testid={`park-card-${park.id}`}
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="truncate uppercase">{park.name}</span>
                        <div className="flex gap-2">
                          {/* Detayları Görüntüle butonu — modal her zaman read-only açılır.
                              Yetkisi olanlar modal içindeki "Düzenle" butonuyla editable'a geçer. */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleParkEdit(park)}
                            data-testid={`view-park-${park.id}`}
                            className="hover:bg-blue-100"
                            title="Detayları Görüntüle"
                          >
                            <Eye className="h-4 w-4 text-blue-600" />
                          </Button>
                          {/* Silme butonu — park.sil yetkisi */}
                          {canDeletePark && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setParkToDelete(park);
                              }}
                              data-testid={`delete-park-${park.id}`}
                              className="hover:bg-red-100"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </CardTitle>
                      <CardDescription>
                        {park.assigned_personnel_ids && park.assigned_personnel_ids.length > 0 ? (
                          <div className="space-y-1">
                            {(() => {
                              // Atanan personelleri rollerine göre ayır, isme göre alfabetik sırala
                              const assignedPersons = park.assigned_personnel_ids
                                .map(pid => personnel.find(p => p.id === pid))
                                .filter(Boolean)
                                .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "tr"));
                              
                              const formens = assignedPersons.filter(p => p.role === 'formen').sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "tr"));
                              // Tutarlı gösterim: izindeki ASIL personel (parkına vekil bakan) HER ZAMAN
                              // önce, vekil sonra; ardından isme göre. Böylece tüm parklarda aynı düzen
                              // (asıl + "vekalet PG X'DA", altında vekil) görünür.
                              const parkGorevlileri = assignedPersons
                                .filter(p => p.role === 'park_gorevlisi')
                                .sort((a, b) => {
                                  const aOwner = getCoverageSubstitutionForPark(park.id, a.id) ? 0 : 1;
                                  const bOwner = getCoverageSubstitutionForPark(park.id, b.id) ? 0 : 1;
                                  if (aOwner !== bOwner) return aOwner - bOwner;
                                  return String(a?.name || "").localeCompare(String(b?.name || ""), "tr");
                                });
                              
                              // Formen ve Bölge Mühendisini belirle (tekil)
                              // İlk park görevlisinden veya ilk formenden al
                              let formen = null;
                              let bolgeMuhendisi = null;
                              
                              if (parkGorevlileri.length > 0) {
                                // İlk park görevlisinin formenini kullan
                                const firstGorevli = parkGorevlileri[0];
                                formen = firstGorevli.formen_id 
                                  ? personnel.find(p => p.id === firstGorevli.formen_id)
                                  : null;
                                bolgeMuhendisi = firstGorevli.bolge_muhendisi_id 
                                  ? personnel.find(p => p.id === firstGorevli.bolge_muhendisi_id)
                                  : (formen?.bolge_muhendisi_id ? personnel.find(p => p.id === formen.bolge_muhendisi_id) : null);
                              } else if (formens.length > 0) {
                                // Park görevlisi yoksa formenden al
                                formen = formens[0];
                                bolgeMuhendisi = formen.bolge_muhendisi_id
                                  ? personnel.find(p => p.id === formen.bolge_muhendisi_id)
                                  : null;
                              }

                              // Kapsam-dışı üst amir fallback'i: Formen/PG panellerinde
                              // kişi listesi üst amirleri içermez → yukarıdaki find'lar
                              // null döner ve kart "(atanmamış)" gösterir. Backend'in
                              // kapsamdan bağımsız çözdüğü resolved_* alanlarına düş.
                              if (!formen && park.resolved_formen_name) {
                                formen = { id: park.resolved_formen_id, name: park.resolved_formen_name };
                              }
                              if (!bolgeMuhendisi && park.resolved_bolge_muhendisi_name) {
                                bolgeMuhendisi = { id: park.resolved_bolge_muhendisi_id, name: park.resolved_bolge_muhendisi_name };
                              }

                              return (
                                <>
                                  {/* Park Görevlileri - Hepsi üstte gruplanmış */}
                                  {parkGorevlileri.length > 0 ? (
                                    <div className="space-y-1">
                                      {parkGorevlileri.map((gorevli, index) => {
                                        // Not: "VEKALETEN ... bakacak" (mavi) rozeti kaldırıldı —
                                        // asıl personeldeki sarı "tarihlerinde vekalet PG X'DA" rozeti
                                        // aynı bilgiyi zaten taşıyor (çift gösterim gereksiz).
                                        const coverageSubstitution = getCoverageSubstitutionForPark(park.id, gorevli.id);
                                        return (
                                          <div key={gorevli.id} className="flex items-center gap-1 text-xs flex-wrap">
                                            <Badge className="text-xs bg-purple-500">Park Görevlisi</Badge>
                                            <span className="font-medium">{gorevli.name}</span>
                                            {coverageSubstitution && (
                                              <Badge className="text-xs bg-amber-100 text-amber-800 border border-amber-300">
                                                {`${new Date(coverageSubstitution.start_date).toLocaleDateString('tr-TR')} - ${new Date(coverageSubstitution.end_date).toLocaleDateString('tr-TR')} tarihlerinde vekalet PG ${coverageSubstitution.substitute_personnel_name || '-' }'DA`}
                                              </Badge>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 text-xs">
                                      <Badge className="text-xs bg-purple-300 text-purple-700">Park Görevlisi</Badge>
                                      <span className="text-gray-400 italic">(atanmamış)</span>
                                    </div>
                                  )}
                                  
                                  {/* Formen - Tek satır */}
                                  {formen ? (
                                    <div className="flex items-center gap-1 text-xs text-gray-600">
                                      <span className="text-gray-400">↳</span>
                                      <Badge className="text-xs bg-yellow-500">Formen</Badge>
                                      <span className="font-medium">{formen.name}</span>
                                    </div>
                                  ) : formens.length === 0 && (
                                    <div className="flex items-center gap-1 text-xs text-gray-600">
                                      <span className="text-gray-400">↳</span>
                                      <Badge className="text-xs bg-yellow-300 text-yellow-700">Formen</Badge>
                                      <span className="text-gray-400 italic">(atanmamış)</span>
                                    </div>
                                  )}
                                  
                                  {/* Bölge Mühendisi - Tek satır */}
                                  {bolgeMuhendisi ? (
                                    <div className="flex items-center gap-1 text-xs text-gray-600">
                                      <span className="text-gray-400 ml-2">↳</span>
                                      <Badge className="text-xs bg-blue-500">Bölge Mühendisi</Badge>
                                      <span className="font-medium">{bolgeMuhendisi.name}</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 text-xs text-gray-600">
                                      <span className="text-gray-400 ml-2">↳</span>
                                      <Badge className="text-xs bg-blue-300 text-blue-700">Bölge Mühendisi</Badge>
                                      <span className="text-gray-400 italic">(atanmamış)</span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <Badge variant="outline">Personel Atanmamış</Badge>
                        )}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
              {/* Faz 2 — Infinite scroll sentinel + durum mesajları */}
              {parkCardsLoading && parkCards.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-500">Park kartları yükleniyor…</div>
              )}
              {!parkCardsLoading && parkCards.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-400">
                  {searchTerm || filterRegion !== "all" || filterPersonnel !== "all"
                    ? "Filtreye uygun park bulunamadı"
                    : "Henüz park yok"}
                </div>
              )}
              {parkCardsHasMore && (
                <div ref={sentinelRef} className="py-6 text-center text-sm text-gray-500">
                  {parkCardsLoadingMore ? (
                    <span>Daha fazla yükleniyor…</span>
                  ) : (
                    <span className="text-gray-400">&nbsp;</span>
                  )}
                </div>
              )}
              {!parkCardsHasMore && parkCards.length > 0 && parkCardsTotal > 0 && (
                <div className="py-4 text-center text-xs text-gray-400">
                  Tüm parklar yüklendi · Toplam {parkCardsTotal}
                </div>
              )}
              </>
            ) : parksViewMode === "map" ? (
              /* Harita Görünümü */
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapIcon className="h-5 w-5 text-green-600" />
                      <span>Park Haritası</span>
                    </div>
                    <Badge variant="outline">
                      {mapParks.length} / {displayedParks.length} koordinatlı park
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {mapParks.length > 0 ? (
                    <div className="h-[600px] rounded-lg overflow-hidden border relative">
                      <MapLayerToggle value={mapLayer} onChange={setMapLayer} floating />

                      {/* Katman paneli — parkın çevresindeki pasif alan / protokollü parkları görünür kılar */}
                      <NearbyLayerPanel
                        base={{
                          ...NEARBY_SOURCES.park,
                          count: mapParks.length,
                          checked: showParksLayer,
                          onChange: setShowParksLayer,
                        }}
                        layers={[
                          {
                            ...NEARBY_SOURCES.pasif,
                            count: pasifPoints.length,
                            checked: showPasifLayer,
                            onChange: setShowPasifLayer,
                          },
                          {
                            ...NEARBY_SOURCES.protokol,
                            count: protokolPoints.length,
                            checked: showProtokolLayer,
                            onChange: setShowProtokolLayer,
                          },
                        ]}
                        loading={loadingNearby}
                        showClear={Boolean(linkLine)}
                        onClearLink={clearLink}
                      />

                      <MapContainer
                        center={[39.925533, 32.866287]}
                        zoom={12}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <MapLayerTile layer={mapLayer} />
                        {/* Filtre değişince görünür noktalara otomatik oturt */}
                        <FitBoundsOnChange bounds={mapFitBounds} boundsKey={mapFitKey} />
                        {/* Pasif alan / protokollü park katmanları — küçük daireler */}
                        <NearbyPointMarkers points={visibleReferences} regionNameOf={getRegionNameForMap} />
                        {/* Park ↔ seçilen referans bağlantı çizgisi */}
                        {linkLine && (
                          <Polyline positions={linkLine} pathOptions={{ color: '#15803d', weight: 2.5, dashArray: '6 6' }}>
                            <Tooltip sticky direction="top">
                              {linkInfo?.refName} — {formatDistance(linkInfo?.distance)}
                            </Tooltip>
                          </Polyline>
                        )}
                        {/* Park sınırları (çizilmiş parklar) — hem harita hem uydu görünümünde.
                            Ana liste summary modda boundaries=null döndürür; bu yüzden özel
                            parkBoundaries kaynağı kullanılır, görünen parklarla kesiştirilir. */}
                        {showParksLayer && (() => {
                          const visibleIds = new Set(displayedParks.map(p => p.id));
                          return (Array.isArray(parkBoundaries) ? parkBoundaries : [])
                            .filter(pb => visibleIds.has(pb.id) && Array.isArray(pb.boundaries) && pb.boundaries.length > 2)
                            .map(pb => (
                              <Polygon
                                key={`park-boundary-${pb.id}`}
                                positions={pb.boundaries}
                                pathOptions={{ color: '#16a34a', fillColor: '#16a34a', fillOpacity: 0.2, weight: 2 }}
                              >
                                <Tooltip sticky direction="top">🌳 {pb.name}</Tooltip>
                              </Polygon>
                            ));
                        })()}
                        {showParksLayer && mapParks
                          .map(park => (
                            <Marker
                              key={park.id}
                              position={[park._lat, park._lng]}
                              icon={pinIcon(NEARBY_SOURCES.park.color)}
                            >
                              <Tooltip direction="top" offset={[0, -12]}>
                                <span className="font-medium">{park.name}</span>
                              </Tooltip>
                              <Popup>
                                <div className="min-w-[250px] max-w-[300px]">
                                  <h3 className="font-bold text-base mb-2 text-green-700 border-b pb-1">{park.name}</h3>
                                  <div className="space-y-1.5 text-sm">
                                    {park.address && (
                                      <p className="flex items-start gap-1">
                                        <span className="font-medium text-gray-600">📍 Adres:</span>
                                        <a
                                          href={buildDirectionsUrl(park._lat, park._lng, park.address)}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-emerald-700 hover:underline"
                                          title="Yol tarifi al ve git"
                                        >
                                          {park.address}
                                        </a>
                                      </p>
                                    )}
                                    {park.district && (
                                      <p><span className="font-medium text-gray-600">🏘️ Mahalle:</span> {park.district}</p>
                                    )}
                                    {regions.find(r => r.id === park.region_id) && (
                                      <p><span className="font-medium text-gray-600">📌 Bölge:</span> {regions.find(r => r.id === park.region_id)?.name}</p>
                                    )}
                                    {park.area && (
                                      <p><span className="font-medium text-gray-600">📐 Alan:</span> {park.area?.toLocaleString('tr-TR')} m²</p>
                                    )}
                                    {park.assigned_personnel_names && park.assigned_personnel_names.length > 0 && (
                                      <p><span className="font-medium text-gray-600">👤 Görevli:</span> {park.assigned_personnel_names.join(', ')}</p>
                                    )}
                                    {/* En yakın pasif alan / protokollü park — "burası neyin yanında?" */}
                                    <NearestPlaces
                                      nearest={nearestByParkId[park.id] || []}
                                      loading={loadingNearby}
                                      title="En yakın"
                                      onSelect={(ref) => focusReference(park, ref)}
                                    />
                                    <div className="flex gap-1 border-t pt-2 mt-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => handleParkEdit(park)}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        {canEditPark ? "Düzenle" : "Detaylar"}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => openInNewTab(buildDirectionsUrl(park._lat, park._lng, park.address))}
                                      >
                                        <Navigation className="h-3 w-3 mr-1" />
                                        Yol Tarifi
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </Popup>
                            </Marker>
                          ))}
                      </MapContainer>
                    </div>
                  ) : (
                    <div className="h-[400px] flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed">
                      <MapIcon className="h-16 w-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 text-lg font-medium">Koordinatı Olan Park Bulunamadı</p>
                      <p className="text-gray-400 text-sm mt-1">Park düzenleyerek koordinat ekleyebilirsiniz</p>
                    </div>
                  )}
                  
                  {/* Koordinatı Eksik Parklar */}
                  {displayedParks.filter(p => !p.latitude || !p.longitude).length > 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm font-medium text-yellow-800 mb-2">
                        ⚠️ Koordinatı Eksik Parklar ({displayedParks.filter(p => !p.latitude || !p.longitude).length} adet)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {displayedParks
                          .filter(p => !p.latitude || !p.longitude)
                          .slice(0, 10)
                          .map(park => (
                            <Badge 
                              key={park.id} 
                              variant="outline" 
                              className="cursor-pointer hover:bg-yellow-100"
                              onClick={() => handleParkEdit(park)}
                            >
                              {park.name}
                            </Badge>
                          ))}
                        {displayedParks.filter(p => !p.latitude || !p.longitude).length > 10 && (
                          <Badge variant="outline" className="bg-yellow-100">
                            +{displayedParks.filter(p => !p.latitude || !p.longitude).length - 10} daha
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Tablo Görünümü */
              <Card>
                <CardContent className="p-0">
                  <div className="grid grid-cols-[200px_1fr_120px_180px_140px_100px] gap-4 px-4 py-2 text-sm font-semibold bg-gray-50 border-b">
                    <div>Park Adı</div>
                    <div>Sorumlu Personel</div>
                    <div>Alan</div>
                    <div>Envanter Özeti</div>
                    <div>Tesisler</div>
                    <div className="text-right">İşlemler</div>
                  </div>
                  <div style={{ maxHeight: `${tableListHeight}px`, overflowY: "auto" }}>
                    {sortedDisplayedParks.map((park, index) => (
                      <div key={park.id || index}>
                        {renderParkRow({ index, style: {}, data: sortedDisplayedParks })}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

    </TabsContent>
  );
}
